
import React, { useState, useEffect, useRef, useMemo } from 'react';
import ErpTemplate from '../../components/ErpTemplate';
import { FaInfoCircle, FaSave, FaTrash } from 'react-icons/fa';
import api from '../../services/api';
import { useHistory } from 'react-router-dom';
import ReactSelect from '../../components/ReactSelect';
import { dataMysql, extract } from '../../util/util';
import { rascunhoRsvp } from '../../util/rascunhoRsvp';
import Data from '../../components/Data';
import { toast } from 'react-toastify';
import moment from 'moment';
import SaveButton from '../../components/SaveButton';
import { OverlayTrigger, Popover, Modal, Button } from 'react-bootstrap';
import CamposVisibilidadeRsvp from './CamposVisibilidadeRsvp';

const CAMPOS_PROTEGIDOS = [
    'rsvp',
    'responsavel_subir_participantes',
    'hotel',
    'planilha_convidados',
    'malha_aerea',
    'link_inscricao',
    'quantidade_hcp',
    'speakers_confirmados',
    'pacote_aereo_quantidade',
    'pacote_hospedagem_quantidade',
    'pacote_transfer_quantidade',
    'numero_inscricoes',
    'pacote_completo_quantidade',
    'questionar_restricao_alimentar',
    'questionar_membro_associacao',
    'questionar_necessidade_mobilidade',
    'questionar_nome_cracha',
    'link_quantidade_inscricao_prazo_sociedade',
    'quantidade_prevista_aereo_valor_aprovado',
    'observacoes_aereo',
    'seguro_viagem',
    'reembolso_aereo',
    'informacoes_transfer',
    'informacoes_inscricoes',
    'previsao_recebimento_listas',
    'lista_hoteis',
    'documentos_diversos',
    'agenda_previa',
    'agenda_previa_texto',
    'emails_copia',
    'numero_hospedagens',
    'arquivocontrolecotas',
    'observacoes_controle_cotas',
];

// Campos que o lock pós-Start NUNCA trava, mesmo preenchidos: são anotações livres, sem impacto
// operacional, que qualquer um com acesso à tela pode reescrever sem liberação da gerente de RSVP.
// Ficam também de fora de CAMPOS_PROTEGIDOS (não contam para o aviso nem para habilitar o Salvar).
// Espelha o back: EventoRsvpPermissaoService::RSVP_FIELDS.
const CAMPOS_SEMPRE_EDITAVEIS = ['observacoes_gerais'];

// Observação cujo conteúdo 'N/A' é gerado pelo próprio checkbox N/A da seção, não digitado pelo
// operador: conta como VAZIO no lock pós-Start (senão marcar N/A antes do Start travaria o campo
// para sempre). Só texto de verdade trava. Espelha o back (NA_FIELDS em isValueEmpty).
const CAMPOS_NA_COMO_VAZIO = ['observacoes_controle_cotas'];

// Campos de anexo do RSVP — cada um vira um registro próprio no rascunho local (keyPath [evento, campo]),
// separado dos campos de texto. filesdocumentosdiversos é uma lista de arquivos (também num registro só).
const ARQUIVO_CAMPOS_SIMPLES = ['fileplanilha', 'filemalhaaerea', 'fileagendaprevia', 'filecontrolecotas'];
const ARQUIVO_CAMPOS = [...ARQUIVO_CAMPOS_SIMPLES, 'filesdocumentosdiversos'];

// Chave binária do anexo → campo real que o lock (campoPodeEditar) avalia. filesdocumentosdiversos
// fica DE FORA de propósito: adicionar documento é sempre liberado (ver EventoRsvpController /
// FILE_FIELD_MAP), então um anexo desses restaurado do rascunho nunca é filtrado.
const ARQUIVO_CAMPO_REAL = {
    fileplanilha: 'planilha_convidados',
    filemalhaaerea: 'malha_aerea',
    fileagendaprevia: 'agenda_previa',
    filecontrolecotas: 'arquivocontrolecotas',
};

// Campos "puxados para a Visibilidade" (ver CamposVisibilidadeRsvp): HCP, speakers e os pacotes.
const CAMPOS_VISIBILIDADE_RSVP = [
    'quantidade_hcp', 'speakers_confirmados',
    'pacote_aereo_quantidade', 'pacote_hospedagem_quantidade', 'pacote_transfer_quantidade',
    'pacote_inscricao_quantidade', 'numero_inscricoes', 'pacote_completo_quantidade',
];

// Quantidades DOS PACOTES: zerado conta como VAZIO (pacote limpo/desligado) no lock pós-Start.
// Vale SÓ para os pacotes — nos demais numéricos (quantidade_hcp, speakers_confirmados) 0 é valor
// deliberado e trava normalmente.
const CAMPOS_QUANTIDADE_PACOTES = [
    'pacote_aereo_quantidade', 'pacote_hospedagem_quantidade', 'pacote_transfer_quantidade',
    'numero_inscricoes', 'pacote_completo_quantidade', 'numero_hospedagens',
];

// Campos exibidos SOMENTE com o RSVP ativo (seção rsvp === 'sim' da tela). São eles que travam a
// desativação do toggle "Ativar RSVP". Os campos pré-RSVP, sempre preenchíveis mesmo com RSVP
// desligado (responsável por subir participantes, planilha de convidados, previsão de recebimento),
// ficam de FORA — não podem exigir limpeza para desativar.
const CAMPOS_RSVP_ATIVO = [
    'malha_aerea_solicitar', 'link_inscricao',
    'questionar_restricao_alimentar', 'questionar_membro_associacao',
    'questionar_necessidade_mobilidade', 'questionar_nome_cracha',
    'link_quantidade_inscricao_prazo_sociedade', 'quantidade_prevista_aereo_valor_aprovado',
    'classevoo', 'reembolso_aereo', 'seguro_viagem', 'observacoes_aereo',
    'informacoes_transfer', 'informacoes_inscricoes', 'hotel',
    'documentos_diversos', 'agenda_previa_texto', 'emails_copia',
    'observacoes_controle_cotas', 'observacoes_gerais',
];

// Campos do evento (abas Geral/Detalhes do cadastro) que devem estar preenchidos antes do Start de RSVP.
// Se algum estiver vazio, o operador é avisado e confirma se quer disparar o Start mesmo assim.
// (Complexidade, Logomarca e Imagem de Fundo ficam de fora; Financeiro não impacta o RSVP.)
const CAMPOS_EVENTO_START = [
    { key: 'cliente', label: 'Cliente' },
    { key: 'identificacao', label: 'Identificação' },
    { key: 'nacionalidade', label: 'Nacional ou Internacional' },
    { key: 'nome', label: 'Nome do Evento' },
    { key: 'tipo', label: 'Tipo' },
    { key: 'tipoRealizacao', label: 'Formato' },
    { key: 'tipoIngresso', label: 'Tipo de Lista' },
    { key: 'observacao', label: 'Descrição', quando: (d) => d.tipo === 'eventointerno' },
    { key: 'link', label: 'Link' },
    { key: 'sistema_aprovado', label: 'Sistema do Cliente Aprovado?' },
    { key: 'data', label: 'Data Inicial' },
    { key: 'hora', label: 'Horário Inicial' },
    { key: 'dataFim', label: 'Data Final' },
    { key: 'horaFim', label: 'Horário Final' },
    { key: 'replanejadoInicio', label: 'Replanejado Início' },
    { key: 'replanejadoFim', label: 'Replanejado Fim' },
    { key: 'dataCompetencia', label: 'Data Competência' },
    { key: 'dataLimiteAceite', label: 'Data Limite Aceite' },
    { key: 'responsavel', label: 'Responsável na Agência' },
    { key: 'diretorresponsavel', label: 'Diretor Responsável' },
    { key: 'responsavelcliente', label: 'Responsável no Cliente' },
    { key: 'local', label: 'Local' },
    { key: 'endereco', label: 'Endereço' },
    { key: 'municipio', label: 'Cidade' },
    { key: 'municipioExterior', label: 'Município Exterior' },
    { key: 'pais', label: 'País' },
    { key: 'enderecocep', label: 'CEP' },
    { key: 'bairro', label: 'Bairro' },
];

// Subconjunto que TRAVA o Start (sem bypass). Os demais campos de CAMPOS_EVENTO_START
// continuam como aviso opcional (podem ser ignorados com "Sim, enviar Start").
const CAMPOS_EVENTO_OBRIGATORIOS_START = ['identificacao', 'tipo', 'tipoRealizacao', 'sistema_aprovado', 'hora', 'local', 'endereco'];

const valorPreenchidoEvento = (valor) => {
    if (valor === null || valor === undefined) return false;
    // Arrays e objetos (ex.: cliente, município) — preenchido se tiver conteúdo.
    // Nunca chamar String() em objeto: alguns vêm sem protótipo e lançam "Cannot convert object to primitive value".
    if (typeof valor === 'object') {
        return Array.isArray(valor) ? valor.length > 0 : Object.keys(valor).length > 0;
    }
    const texto = String(valor).trim();
    return texto !== '' && texto !== '[]' && texto !== 'null';
};

const criarLinhaHotel = (item = {}) => {
    const base = {
        hotel: item.hotel ?? '',
        nomeHotel: item.nomeHotel ?? item.nome_hotel ?? '',
        data_checkin: item.data_checkin ?? '',
        hora_checkin: item.hora_checkin ?? '',
        data_checkout: item.data_checkout ?? '',
        hora_checkout: item.hora_checkout ?? '',
        quantidade_pessoas: item.quantidade_pessoas !== undefined && item.quantidade_pessoas !== null && item.quantidade_pessoas !== ''
            ? Number(item.quantidade_pessoas)
            : '',
    };
    if ('bloqueados_single' in item) base.bloqueados_single = item.bloqueados_single !== '' && item.bloqueados_single != null ? Number(item.bloqueados_single) : '';
    if ('bloqueados_double' in item) base.bloqueados_double = item.bloqueados_double !== '' && item.bloqueados_double != null ? Number(item.bloqueados_double) : '';
    return base;
};

const BotaoObservacao = ({ campo, observacao, onChange, className = '', style = {}, disabled }) => {
    const possuiObservacao = !!String(observacao || '').trim();
    const textareaRef = useRef(null);
    const popover = (
        <Popover
            id={`observacao-${campo.replace(/[^a-zA-Z0-9_-]/g, '-')}`}
            style={{ width: 280, maxWidth: 'calc(100vw - 32px)' }}
        >
            <Popover.Title as="h3">Observação</Popover.Title>
            <Popover.Content>
                <textarea
                    ref={textareaRef}
                    className="form-control"
                    rows="4"
                    placeholder="Digite uma observação para este campo..."
                    value={observacao || ''}
                    onChange={(e) => onChange(campo, e.target.value)}
                    style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical' }}
                />
            </Popover.Content>
        </Popover>
    );

    return (
        <OverlayTrigger trigger="click" placement="auto" rootClose overlay={popover} onEntered={() => textareaRef.current?.focus()}>
            <button
                disabled={disabled}
                type="button"
                className={`btn btn-sm p-0 border-0 bg-transparent d-inline-flex align-items-center ${possuiObservacao ? 'text-info' : 'text-muted'} ${className}`}
                title="Observação"
                style={style}
            >
                <FaInfoCircle size={12} />
            </button>
        </OverlayTrigger>
    );
};

const LabelComObservacao = ({ children, campo, observacao, onChange, className = '', htmlFor, disabled }) => {
    return (
        <div className="d-inline-flex align-items-center">
            <label className={`${className} mb-0`} htmlFor={htmlFor}>{children}</label>
            <BotaoObservacao campo={campo} observacao={observacao} onChange={onChange} disabled={disabled} />
        </div>
    );
};

export default function CadastroEventoRsvpDadosIniciais({ match }) {
    const id = match.params.id;
    const [dados, setDados] = useState({
        evento_rsvp_data: {}
    });
    const [dadosOriginais, setDadosOriginais] = useState({
        rsvp: 'nao',
        evento_rsvp_data: {}
    });
    const [arquivosSalvos, setArquivosSalvos] = useState([]);
    const [usuarioPermissaoSelecionado, setUsuarioPermissaoSelecionado] = useState([]);
    const [isSubmittingPermissao, setIsSubmittingPermissao] = useState(false);
    const [removendoPermissaoId, setRemovendoPermissaoId] = useState(null);
    const [classeVooOptions, setClasseVooOptions] = useState([]);
    const [naoSeAplicaState, setNaoSeAplica] = useState({
        planilha_convidados: false,
        malha_aerea: false,
        previsao_recebimento_listas: false,
        controle_cotas: false,
    });
    const [aDefinirState, setADefinirState] = useState({
        hotel: false,
        controle_cotas: false,
    });
    const [colunasHotelAtivas, setColunasHotelAtivas] = useState({ single: false, double: false });
    const [showModalRsvpSemLocal, setShowModalRsvpSemLocal] = useState(false);
    const [showModalCamposEvento, setShowModalCamposEvento] = useState(false);
    const [camposEventoFaltantes, setCamposEventoFaltantes] = useState([]);
    const [camposEventoObrigatoriosFaltantes, setCamposEventoObrigatoriosFaltantes] = useState([]);
    const [enviandoNotificacaoRsvp, setEnviandoNotificacaoRsvp] = useState(false);
    const [agendaPreviaMode, setAgendaPreviaMode] = useState(null);
    const planilhaConvidadosInputRef = useRef(null);
    const malhaAereaInputRef = useRef(null);
    const agendaPreviaInputRef = useRef(null);
    const controleCoatasArquivoInputRef = useRef(null);
    const history = useHistory();
    const token = localStorage.getItem('token');
    const perfil = localStorage.getItem("perfil");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(!!id);
    // Rascunho local (IndexedDB): modal de recuperação + guards de auto-save.
    const [showModalRascunho, setShowModalRascunho] = useState(false);
    const [rascunhoPendente, setRascunhoPendente] = useState(null);
    const [autoSaveArmado, setAutoSaveArmado] = useState(false);
    const podeAutoSalvarRef = useRef(false); // só liga depois que o operador decide sobre o rascunho
    const ultimoSalvoRef = useRef(null); // última fatia gravada, para gravar só o que muda (por campo)
    const ultimoCamposRef = useRef(null); // último snapshot de texto gravado (sem salvoEm), p/ não gravar rascunho igual
    const rascunhoStore = useMemo(() => (id ? rascunhoRsvp(id) : null), [id]);
    const podeGerenciarPermissoes = ['gerentersvp', 'coordenadorrsvp', 'admin', 'diretor'].includes(perfil);
    const existeRegistroRsvp = Boolean(dadosOriginais?.evento_rsvp_data?.id);
    const podeEditarCamposPreenchidos = podeGerenciarPermissoes || dados?.evento_rsvp_data?.pode_editar_campos_preenchidos === true;
    const permissoesAtivas = dados?.evento_rsvp_data?.participantes_permissao_alteracao || [];
    const possuiHotel = dados?.evento_rsvp_data?.hotel === 'sim';
    const possuiHotelSelecionado = (dados?.evento_rsvp_data?.lista_hoteis || []).length > 0;
    const eventoInternacional = dados?.nacionalidade === 'internacional';
    const ehAbbvie = (() => {
        const nome = (dados?.cliente?.nome ?? '').toLowerCase();
        const fantasia = (dados?.cliente?.nomefantasia ?? '').toLowerCase();
        return nome.includes('abbvie') || fantasia.includes('abbvie') || nome.includes('allergan') || fantasia.includes('allergan');
    })();
    const temArquivoAgendaPrevia = !!(dados.evento_rsvp_data?.agenda_previa_arquivo?.id) || !!(dados.evento_rsvp_data?.fileagendaprevia);
    const temTextoAgendaPrevia = !!(dados.evento_rsvp_data?.agenda_previa_texto || '').trim();
    const agendaPreviaBloqueada = temArquivoAgendaPrevia || temTextoAgendaPrevia;
    const observacaoCampo = (campo) => dados?.evento_rsvp_data?.observacoes_campos?.[campo] || '';

    /**
     * @param {keyof typeof naoSeAplicaState} campo
     */
    const naoSeAplica = (campo) => naoSeAplicaState[campo] === true;

    /**
     * @param {keyof typeof aDefinirState} campo
     */
    const aDefinir = (campo) => aDefinirState[campo] === true;

    const limparCampos = {
        planilha_convidados: ['fileplanilha'],
        malha_aerea: ['filemalhaaerea'],
        previsao_recebimento_listas: ['previsao_recebimento_listas'],
        controle_cotas: ['filecontrolecotas'],
    };


    /**
     * @param {keyof typeof limparCampos} campo
     */
    const limparCampo = (campo) => {

        const inputRefs = {
            planilha_convidados: planilhaConvidadosInputRef,
            malha_aerea: malhaAereaInputRef,
            controle_cotas: controleCoatasArquivoInputRef,
        };

        const rsvp = { ...(dados.evento_rsvp_data || {}) };
        (limparCampos[campo] || []).forEach((nomeCampo) => {
            rsvp[nomeCampo] = null;
        });

        if (inputRefs[campo]?.current) {
            inputRefs[campo].current.value = '';
        }
        setDados({ ...dados, evento_rsvp_data: rsvp });
    }

    /**
     * @param {keyof typeof aDefinirState} campo
     */
    const atualizarADefinir = (campo, checked) => {

        setADefinirState(prev => ({ ...prev, [campo]: checked }));

        if(checked)
            limparCampo(campo)

    };

    /**
     * @param {keyof typeof naoSeAplicaState} campo
     */
    const atualizarNaoSeAplica = (campo, checked) => {

        setNaoSeAplica(prev => ({
            ...prev,
            [campo]: checked,
        }));

        if(checked) {
            limparCampo(campo)
            // Exclusividade do 3º estado: marcar N/A desmarca a flag (consta no cliente / solicitar).
            if (campo === 'planilha_convidados' || campo === 'malha_aerea') {
                const flag = campo === 'planilha_convidados' ? 'planilha_consta_cliente' : 'malha_aerea_solicitar';
                setDados(prev => ({ ...prev, evento_rsvp_data: { ...(prev.evento_rsvp_data || {}), [flag]: false } }));
            }
        }

    };

    /**
     * Flag do 3º estado de planilha/malha ("consta no cliente" / "solicitar"): exclusiva com N/A e arquivo.
     */
    const atualizarFlagExclusiva = (campo, flag, checked) => {
        setDados(prev => {
            const rsvp = { ...(prev.evento_rsvp_data || {}) };
            rsvp[flag] = checked;
            if (checked) {
                if (campo === 'planilha_convidados') { rsvp.planilha_convidados = null; rsvp.planilha_arquivo = null; rsvp.fileplanilha = null; }
                if (campo === 'malha_aerea') { rsvp.malha_aerea = null; rsvp.malha_aerea_arquivo = null; rsvp.filemalhaaerea = null; }
            }
            return { ...prev, evento_rsvp_data: rsvp };
        });
        if (checked) setNaoSeAplica(prev => ({ ...prev, [campo]: false }));
    };
    useEffect(()=>{
        setDados(prev=>({...prev,evento_rsvp_data:{...prev.evento_rsvp_data,
            observacoes_controle_cotas:naoSeAplica('controle_cotas')?"N/A":""
        }}))
    },[naoSeAplica('controle_cotas')])
    // Previsão só pode ficar N/A quando a planilha está N/A; se a planilha não está N/A, desmarca o N/A da previsão.
    useEffect(()=>{
        if (!naoSeAplica('planilha_convidados')) {
            setNaoSeAplica(prev => prev.previsao_recebimento_listas ? { ...prev, previsao_recebimento_listas: false } : prev);
        }
    },[naoSeAplica('planilha_convidados')])
    const atualizarObservacaoCampo = (campo, valor) => {
        const rsvp = { ...(dados.evento_rsvp_data || {}) };
        const observacoes = { ...(rsvp.observacoes_campos || {}) };
        const texto = String(valor || '').trim();

        if (texto) {
            observacoes[campo] = valor;
        } else {
            delete observacoes[campo];
        }

        rsvp.observacoes_campos = Object.keys(observacoes).length > 0 ? observacoes : null;
        setDados({ ...dados, evento_rsvp_data: rsvp });
    };
    const labelObservacao = (campo, label, props = {}) => (
        <LabelComObservacao
            campo={campo}
            observacao={observacaoCampo(campo)}
            onChange={atualizarObservacaoCampo}
            {...props}
        >
            {label}
        </LabelComObservacao>
    );
    const botaoObservacao = (campo) => (
        <BotaoObservacao
            campo={campo}
            observacao={observacaoCampo(campo)}
            onChange={atualizarObservacaoCampo}
        />
    );

    const carregarOpcoesHotel = (input) => {
        return api({
            method: 'get',
            action: '/participante',
            token,
            params: {
                nome: input,
                tipoCadastro: ["hotel"],
                situacaoRegistro: "ativo",
            }
        })
            .then(response => {
                const options = (response.data.data || []).map((registro) => {
                    const label = registro.codigo + ' - ' + registro.nome +
                        (registro.enderecomunicipio ? ' - ' + registro.enderecomunicipio.nome + '-' + registro.enderecomunicipio.uf : '') +
                        (registro.cnpj ? ' (' + registro.cnpj + ')' : '') +
                        (registro.cpf ? ' (' + registro.cpf + ')' : '') +
                        (registro.nomefantasia ? ' - ' + registro.nomefantasia : '');

                    return { value: registro.id, label };
                });

                return options;
            })
            .catch(() => []);
    };

    const atualizarListaHoteis = (callback) => {
        const rsvp = { ...(dados.evento_rsvp_data || {}) };
        const listaAtual = Array.isArray(rsvp.lista_hoteis)
            ? rsvp.lista_hoteis.map((item) => criarLinhaHotel(item))
            : [];

        rsvp.lista_hoteis = callback(listaAtual);
        setDados({ ...dados, evento_rsvp_data: rsvp });
    };

    const atualizarLinhaHotel = (index, callback) => {
        atualizarListaHoteis((listaAtual) => {
            const lista = [...listaAtual];
            lista[index] = callback(criarLinhaHotel(lista[index]));
            return lista;
        });
    };

    const removerLinhaHotel = (indexRemovido) => {
        const rsvp = { ...(dados.evento_rsvp_data || {}) };
        const listaAtual = Array.isArray(rsvp.lista_hoteis)
            ? rsvp.lista_hoteis.map((item) => criarLinhaHotel(item))
            : [];
        const observacoesAtuais = { ...(rsvp.observacoes_campos || {}) };
        const observacoesAtualizadas = {};

        Object.entries(observacoesAtuais).forEach(([campo, observacao]) => {
            const match = campo.match(/^lista_hoteis\.(\d+)\.(.+)$/);
            if (!match) {
                observacoesAtualizadas[campo] = observacao;
                return;
            }

            const index = Number(match[1]);
            const propriedade = match[2];
            if (index < indexRemovido) {
                observacoesAtualizadas[campo] = observacao;
            } else if (index > indexRemovido) {
                observacoesAtualizadas[`lista_hoteis.${index - 1}.${propriedade}`] = observacao;
            }
        });

        rsvp.lista_hoteis = listaAtual.filter((_, index) => index !== indexRemovido);
        rsvp.observacoes_campos = Object.keys(observacoesAtualizadas).length > 0 ? observacoesAtualizadas : null;
        setDados({ ...dados, evento_rsvp_data: rsvp });
    };

    const adicionarColunaHotel = (tipo) => {
        setColunasHotelAtivas(prev => ({ ...prev, [tipo]: true }));
        atualizarListaHoteis(lista =>
            lista.map(row => ({ ...row, [`bloqueados_${tipo}`]: '' }))
        );
    };

    const removerColunaHotel = (tipo) => {
        setColunasHotelAtivas(prev => ({ ...prev, [tipo]: false }));
        atualizarListaHoteis(lista =>
            lista.map(row => {
                const updated = { ...row };
                delete updated[`bloqueados_${tipo}`];
                return updated;
            })
        );
    };

    const listaTemConteudo = (lista = []) => {
        if (!Array.isArray(lista) || lista.length === 0) {
            return false;
        }

        return lista.some((item) => (
            (item?.hotel ?? '') !== ''
            || (item?.nomeHotel ?? item?.nome_hotel ?? '') !== ''
            || (item?.data_checkin ?? '') !== ''
            || (item?.data_checkout ?? '') !== ''
            || item?.quantidade_pessoas === 0
            || (item?.quantidade_pessoas ?? '') !== ''
        ));
    };

    const valorOriginalCampo = (campo) => {
        const matchListaHoteis = campo.match(/^lista_hoteis\[(\d+)\]\.(.+)$/);
        if (matchListaHoteis) {
            const index = Number(matchListaHoteis[1]);
            const propriedade = matchListaHoteis[2];
            return (dadosOriginais?.evento_rsvp_data?.lista_hoteis || [])?.[index]?.[propriedade];
        }

        switch (campo) {
            case 'rsvp':
                return dadosOriginais?.rsvp;
            case 'planilha_convidados':
                return dadosOriginais?.evento_rsvp_data?.planilha_convidados
                    ?? dadosOriginais?.evento_rsvp_data?.planilha_arquivo?.id
                    ?? null;
            case 'malha_aerea':
                return dadosOriginais?.evento_rsvp_data?.malha_aerea
                    ?? dadosOriginais?.evento_rsvp_data?.malha_aerea_arquivo?.id
                    ?? null;
            case 'documentos_diversos':
                return dadosOriginais?.evento_rsvp_data?.documentos_diversos
                    ?? dadosOriginais?.evento_rsvp_data?.documentos_diversos_arquivos
                    ?? [];
            case 'agenda_previa':
                return dadosOriginais?.evento_rsvp_data?.agenda_previa
                    ?? dadosOriginais?.evento_rsvp_data?.agenda_previa_arquivo?.id
                    ?? null;
            case 'arquivocontrolecotas':
                return dadosOriginais?.evento_rsvp_data?.arquivocontrolecotas
                    ?? dadosOriginais?.evento_rsvp_data?.arquivo_controle_cotas?.id
                    ?? null;
            case 'lista_hoteis':
                return dadosOriginais?.evento_rsvp_data?.lista_hoteis || [];
            default:
                return dadosOriginais?.evento_rsvp_data?.[campo];
        }
    };

    const valorEhVazio = (campo, valor) => {
        if (!existeRegistroRsvp) {
            return true;
        }

        // Pacote com quantidade zerada = vazio (limpo): continua editável pós-Start.
        if (CAMPOS_QUANTIDADE_PACOTES.includes(campo) && !isNaN(Number(valor)) && Number(valor) === 0) {
            return true;
        }

        // Observação salva como 'N/A' = vazio: o texto veio do checkbox N/A, não do operador.
        if (CAMPOS_NA_COMO_VAZIO.includes(campo) && `${valor ?? ''}`.trim().toUpperCase() === 'N/A') {
            return true;
        }

        if (/^lista_hoteis\[\d+\]\.hotel$/.test(campo)) {
            return !valor || `${valor}`.trim() === '';
        }

        if (/^lista_hoteis\[\d+\]\./.test(campo)) {
            return valor == null || `${valor}`.trim() === '';
        }

        if (campo === 'lista_hoteis') {
            return !listaTemConteudo(valor || []);
        }

        if (campo === 'documentos_diversos') {
            if (Array.isArray(valor)) {
                return valor.length === 0;
            }
            return !valor || `${valor}`.trim() === '';
        }

        if (['planilha_convidados', 'agenda_previa', 'arquivocontrolecotas'].includes(campo)) {
            return !valor;
        }

        if (campo === 'rsvp') {
            return valor == null || `${valor}`.trim() === '';
        }

        if (Array.isArray(valor)) {
            return valor.length === 0;
        }

        if (typeof valor === 'number') {
            return valor == null;
        }

        return valor == null || `${valor}`.trim() === '';
    };

    // Start de RSVP já enviado? (started_at preenchido). Antes do Start, TODOS os campos são
    // livremente editáveis; depois, valem as regras normais (campo preenchido só com liberação).
    const startRsvpEnviado = !!dados?.evento_rsvp_data?.started_at;

    // O bloqueio de campos preenchidos só vale após o Start E com responsável atribuído ao evento
    // (espelha o back: EventoRsvpPermissaoService::camposBloqueadosRsvp). Sem responsável, libera.
    const temResponsavelAtribuido = (dados?.responsavel || []).some((registro) => !!registro?.id);
    const bloqueioCamposAtivo = startRsvpEnviado && temResponsavelAtribuido;

    const campoPodeEditar = (campo) => (
        CAMPOS_SEMPRE_EDITAVEIS.includes(campo)
        || !bloqueioCamposAtivo
        || !existeRegistroRsvp
        || podeEditarCamposPreenchidos
        || valorEhVazio(campo, valorOriginalCampo(campo))
    );

    // Switches (pacotes e questionar_*): após o Start podem ser LIGADOS, mas não desligados.
    // O bloqueio olha o valor SALVO (dadosOriginais): desligar algo já salvo como ligado é vetado;
    // ligar é sempre livre — inclusive um switch salvo como 'nao', que para o lock de campo comum
    // contaria como "preenchido" e travaria desligado. Desfazer um liga da própria sessão (ainda
    // não salvo) continua permitido.
    const switchTravadoLigado = (campo) => {
        if (!bloqueioCamposAtivo || !existeRegistroRsvp || podeEditarCamposPreenchidos) return false;
        const original = dadosOriginais?.evento_rsvp_data?.[campo];
        if (original === null || original === undefined || original === '' || original === 'nao') return false;
        if (!isNaN(Number(original)) && Number(original) === 0) return false; // quantidade zerada = desligado
        return true; // 'sim' ou pacote com quantidade salva (> 0) = ligado no servidor
    };

    const possuiCampoPreenchidoTravado = bloqueioCamposAtivo
        && existeRegistroRsvp
        && !podeEditarCamposPreenchidos
        && CAMPOS_PROTEGIDOS.some((campo) => !valorEhVazio(campo, valorOriginalCampo(campo)));

    const podeSalvar = !existeRegistroRsvp
        || podeEditarCamposPreenchidos
        || !bloqueioCamposAtivo
        || CAMPOS_PROTEGIDOS.some((campo) => valorEhVazio(campo, valorOriginalCampo(campo)));

    const obrigatorio = <span className="text-danger">*</span>;

    const opcoesUsuariosPermissao = (dados?.responsavel || [])
        .filter((registro) => !!registro?.id && !!registro?.nome)
        .map((registro) => ({
            value: registro.id,
            label: registro.nome
        }));

    useEffect(() => {
        buscar();
        buscarClasseVoo();
        // eslint-disable-next-line
    }, []);

    const buscarClasseVoo = () => {
        api({
            method: 'get',
            action: '/classeVoo',
            token: token,
        }).then(response => {
            const options = (response.data.data || []).map((registro) => ({
                value: registro.id,
                label: registro.nome,
            }));
            const optionsOrdenadas = [
                ...options.filter((option) => option.label === 'N/A'),
                ...options.filter((option) => option.label !== 'N/A'),
            ];
            setClasseVooOptions(optionsOrdenadas);
        }).catch(() => {
            setClasseVooOptions([]);
        });
    };

    const buscar = () => {
        setIsLoading(true);
        if (id) {
            api({
                method: "get",
                action: '/evento/' + id,
                token: token,
            }).then(response => {
                var dadosRecebidos = extract({
                    jsonLaravel: response.data.data,
                    chaves: []
                });

                // Carregar campo rsvp diretamente (não é relacionamento, é string 'sim' ou 'nao')
                if (response.data.data.rsvp !== undefined) {
                    dadosRecebidos.rsvp = response.data.data.rsvp;
                } else {
                    dadosRecebidos.rsvp = 'nao';
                }

                dadosRecebidos.visibilidade_rsvp = response.data.data.visibilidade_rsvp ?? 'nao';
                dadosRecebidos.cliente = response.data.data.cliente ?? null;

                // Helper para montar dados do RSVP
                const montarDadosRsvp = (rsvpData) => {
                    if (!rsvpData) return {};

                    // Helper para converter string JSON para array ou manter array
                    const parseJsonOrArray = (value) => {
                        if (!value) return [];
                        if (Array.isArray(value)) return value;
                        if (typeof value === 'string') {
                            try {
                                return JSON.parse(value);
                            } catch (e) {
                                return [];
                            }
                        }
                        return [];
                    };

                    // Normalizar lista_hoteis: array de { hotel, nomeHotel, a_definir, data_checkin, hora_checkin, data_checkout, hora_checkout, quantidade_pessoas }
                    const rawLista = parseJsonOrArray(rsvpData.lista_hoteis);
                    const lista_hoteis = rawLista.length > 0
                        ? rawLista.map((item) => criarLinhaHotel(item))
                        : [];

                    return {
                        ...rsvpData,
                        data_saida: parseJsonOrArray(rsvpData.data_saida),
                        lista_hoteis,
                        classevoo: rsvpData.classevoo?.id ?? rsvpData.classevoo ?? null,
                        // O switch do Pacote Inscrição usa pacote_inscricao_quantidade (espelho de
                        // tela), mas a coluna persistida é numero_inscricoes — semeia o espelho para
                        // o switch refletir o salvo. Zerado conta como desligado.
                        pacote_inscricao_quantidade: (rsvpData.numero_inscricoes ?? null) !== null && Number(rsvpData.numero_inscricoes) !== 0
                            ? rsvpData.numero_inscricoes
                            : null,
                        // Manter os objetos de arquivo originais para exibição
                        planilha_arquivo: rsvpData.planilha_arquivo,
                        malha_aerea_arquivo: rsvpData.malha_aerea_arquivo,
                        agenda_previa_arquivo: rsvpData.agenda_previa_arquivo,
                        arquivo_controle_cotas: rsvpData.arquivo_controle_cotas
                    };
                };

                // Carregar dados de RSVP
                if (response.data.data.evento_rsvp_data) {
                    dadosRecebidos.evento_rsvp_data = montarDadosRsvp(response.data.data.evento_rsvp_data);

                    // Carregar arquivos salvos de documentos diversos
                    if (response.data.data.evento_rsvp_data.documentos_diversos_arquivos) {
                        setArquivosSalvos(response.data.data.evento_rsvp_data.documentos_diversos_arquivos);
                    }

                    const rsvp = response.data.data.evento_rsvp_data;
                    if(response.data.data.rsvp=='sim'||startRsvpEnviado){
                        setNaoSeAplica(prev => ({
                            ...prev,
                            planilha_convidados: !rsvp.planilha_arquivo && !rsvp.planilha_consta_cliente,
                            malha_aerea: !rsvp.malha_aerea_arquivo && !rsvp.malha_aerea_solicitar,
                            // Previsão só pode vir N/A por padrão quando a planilha está N/A (sem anexo e sem "consta no cliente").
                            previsao_recebimento_listas: (!rsvp.planilha_arquivo && !rsvp.planilha_consta_cliente) && !rsvp.previsao_recebimento_listas,
                            controle_cotas: rsvp.observacoes_controle_cotas === 'N/A',
                        }));
                        setADefinirState(prev => ({
                            ...prev,
                            hotel: rsvp.hotel === 'sim' && (!rsvp.lista_hoteis || rsvp.lista_hoteis.length === 0),
                            controle_cotas: !rsvp?.arquivo_controle_cotas,
                        }));
                    }

                    const listaHoteis = dadosRecebidos.evento_rsvp_data?.lista_hoteis ?? [];
                    setColunasHotelAtivas({
                        single: listaHoteis.some(h => 'bloqueados_single' in h),
                        double: listaHoteis.some(h => 'bloqueados_double' in h),
                    });

                    if (rsvp.agenda_previa_texto) {
                        setAgendaPreviaMode('texto');
                    } else if (rsvp.agenda_previa_arquivo) {
                        setAgendaPreviaMode('arquivo');
                    }
                } else {
                    dadosRecebidos.evento_rsvp_data = {};
                }

                setDadosOriginais({
                    rsvp: dadosRecebidos.rsvp,
                    visibilidade_rsvp: dadosRecebidos.visibilidade_rsvp,
                    evento_rsvp_data: JSON.parse(JSON.stringify(dadosRecebidos.evento_rsvp_data || {}))
                });
                setDados(dadosRecebidos);
                verificarRascunhoPendente();
            }).catch(err => {
                toast.error("Erro ao carregar dados do evento.");
            }).finally(() => { setIsLoading(false); });
        } else {
            setIsLoading(false);
        }
    };


    // Monta a ENTRADA DE TEXTO do rascunho (uma só): campos do RSVP sem os anexos + flags + estados
    // auxiliares. As relações do servidor (cliente, responsável...) ficam de fora — recarregadas no
    // open; o rascunho é sobreposto por cima.
    const montarCampos = () => {
        const rsvpData = { ...(dados?.evento_rsvp_data || {}) };
        ARQUIVO_CAMPOS.forEach((k) => { delete rsvpData[k]; });
        // Permissões de alteração são geridas fora do form (endpoint próprio do gerente/admin) e
        // recolocadas do servidor no restore — não fazem parte do rascunho. Incluí-las fazia o
        // buscar() pós-permissão gravar um rascunho e disparar o modal na próxima abertura.
        delete rsvpData.participantes_permissao_alteracao;
        Object.keys(rsvpData).forEach((k) => {
            if (moment.isMoment(rsvpData[k])) rsvpData[k] = rsvpData[k].toISOString();
        });
        return {
            rsvp: dados?.rsvp ?? 'nao',
            visibilidade_rsvp: dados?.visibilidade_rsvp ?? 'nao',
            eventoRsvpData: rsvpData,
            naoSeAplicaState,
            aDefinirState,
            colunasHotelAtivas,
            agendaPreviaMode,
            arquivosSalvos,
            salvoEm: new Date().toISOString(),
        };
    };

    // Chave de comparação do rascunho de texto: o snapshot sem o salvoEm (que muda a cada chamada).
    // Dois snapshots com a mesma chave = nada mudou de fato → não regrava o rascunho.
    const chaveCampos = (campos) => JSON.stringify({ ...campos, salvoEm: null });

    // Coleta os anexos atuais no estado, por campo. Cada um é gravado num registro próprio.
    const coletarArquivos = () => {
        const rsvpData = dados?.evento_rsvp_data || {};
        const out = {};
        ARQUIVO_CAMPOS_SIMPLES.forEach((k) => {
            if (rsvpData[k] instanceof Blob) out[k] = rsvpData[k];
        });
        const docs = rsvpData.filesdocumentosdiversos;
        if (Array.isArray(docs) && docs.some((f) => f instanceof Blob)) {
            out.filesdocumentosdiversos = docs;
        }
        return out;
    };

    // Um campo conta como "preenchido" se tem valor útil COMO EXIBIDO NA TELA: arquivo, array não
    // vazio, flag ligada ('sim'/true) ou texto ≠ ''. Não contam: vazios, 'nao' (default dos toggles
    // sim/não) e número zerado — pacote ligado grava 0, então "preencheu e apagou" volta a liberar.
    const rsvpValorPreenchido = (valor) => {
        if (valor === null || valor === undefined || valor === false) return false;
        if (valor === true) return true;
        if (valor instanceof Blob) return true;
        if (Array.isArray(valor)) return valor.length > 0;
        const s = String(valor).trim();
        if (s === '' || s === 'nao' || s === 'null' || s === '[]') return false;
        if (!isNaN(Number(s))) return Number(s) !== 0;
        return true;
    };

    // Há algum campo da Visibilidade preenchido? (HCP, speakers, pacotes — zerados não contam)
    const visibilidadeTemCampoPreenchido = () => {
        const r = dados?.evento_rsvp_data || {};
        return CAMPOS_VISIBILIDADE_RSVP.some((c) => rsvpValorPreenchido(r[c]));
    };

    // Há algum campo preenchido na seção exibida com o RSVP ativo? (visibilidade + campos da seção
    // + hotéis + anexos da seção). Planilha de convidados e demais campos pré-RSVP ficam de fora —
    // são sempre preenchíveis, com RSVP ligado ou não.
    const rsvpTemCampoPreenchido = () => {
        const r = dados?.evento_rsvp_data || {};
        if (dados.visibilidade_rsvp!=='sim'&&visibilidadeTemCampoPreenchido()) return true;
        if (Array.isArray(r.lista_hoteis) && listaTemConteudo(r.lista_hoteis)) return true;
        if (['malha_aerea_arquivo', 'agenda_previa_arquivo', 'arquivo_controle_cotas'].some((c) => r[c]?.id)) return true;
        if (['filemalhaaerea', 'fileagendaprevia', 'filecontrolecotas', 'filesdocumentosdiversos'].some((c) => rsvpValorPreenchido(r[c]))) return true;
        return CAMPOS_RSVP_ATIVO.some((c) => rsvpValorPreenchido(r[c]));
    };

    // Mostra o anexo LOCAL (File no state, ex.: vindo do rascunho restaurado) pelo nome — o input
    // nativo não pode ser preenchido por segurança, então sem isso um rascunho com arquivo aparece
    // como "nenhum ficheiro selecionado". Permite remover (limpa o input e o state).
    const renderArquivoRascunho = (campo, inputRef) => {
        const file = dados?.evento_rsvp_data?.[campo];
        if (!(file instanceof Blob)) return null;
        return (
            <div className="mt-2 d-flex align-items-center justify-content-between">
                <small className="text-muted">
                    <strong>Arquivo selecionado:</strong>
                    <span className="ms-1">{file.name}</span>
                </small>
                <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    title="Remover arquivo selecionado"
                    onClick={() => {
                        if (inputRef?.current) inputRef.current.value = '';
                        setDados(prev => ({ ...prev, evento_rsvp_data: { ...prev.evento_rsvp_data, [campo]: null } }));
                    }}
                >
                    <FaTrash size={12} />
                </button>
            </div>
        );
    };

    // Sobrepõe o rascunho sobre os dados já carregados do servidor (prev): mantém as relações
    // frescas e recoloca só o que o operador editou, com os anexos recolados em evento_rsvp_data.
    const aplicarRascunho = (pend) => {
        const campos = pend?.campos || {};
        const arquivos = pend?.arquivos || {};
        setDados((prev) => ({
            ...prev,
            rsvp: campos.rsvp ?? prev.rsvp,
            visibilidade_rsvp: campos.visibilidade_rsvp ?? prev.visibilidade_rsvp,
            evento_rsvp_data: {
                ...(prev.evento_rsvp_data || {}),
                ...Object.fromEntries(Object.entries(campos.eventoRsvpData || {}).filter(([k]) => campoPodeEditar(k))),
                ...Object.fromEntries(Object.entries(arquivos).filter(([k]) => !ARQUIVO_CAMPO_REAL[k] || campoPodeEditar(ARQUIVO_CAMPO_REAL[k]))),
                participantes_permissao_alteracao: prev.evento_rsvp_data?.participantes_permissao_alteracao,
            },
        }));
        if (Array.isArray(campos.arquivosSalvos)) setArquivosSalvos(campos.arquivosSalvos);
        if (campos.naoSeAplicaState) setNaoSeAplica(campos.naoSeAplicaState);
        if (campos.aDefinirState) setADefinirState(campos.aDefinirState);
        if (campos.colunasHotelAtivas) setColunasHotelAtivas(campos.colunasHotelAtivas);
        setAgendaPreviaMode(campos.agendaPreviaMode ?? null);
    };

    // Chamado ao terminar o carregamento do servidor: se houver rascunho, pergunta ao operador;
    // senão, arma o auto-save direto.
    const verificarRascunhoPendente = async () => {
        if (!rascunhoStore) { setAutoSaveArmado(true); return; }
        const [campos, arquivos] = await Promise.all([
            rascunhoStore.carregarCampos(),
            rascunhoStore.carregarArquivos(),
        ]);
        const temRascunho = !!campos || (arquivos && Object.keys(arquivos).length > 0);
        if (temRascunho) {
            setRascunhoPendente({ campos: campos || {}, arquivos: arquivos || {} });
            setShowModalRascunho(true);
        } else {
            setAutoSaveArmado(true);
        }
    };

    const restaurarRascunho = () => {
        if (rascunhoPendente) aplicarRascunho(rascunhoPendente);
        setShowModalRascunho(false);
        setRascunhoPendente(null);
        setAutoSaveArmado(true);
    };

    const descartarRascunho = async () => {
        setShowModalRascunho(false);
        setRascunhoPendente(null);
        if (rascunhoStore) await rascunhoStore.limpar();
        setAutoSaveArmado(true);
    };

    const adicionarPermissaoAlteracao = async () => {
        if (!usuarioPermissaoSelecionado?.length) {
            toast.error('Selecione ao menos um usuário para liberar a alteração.');
            return;
        }

        setIsSubmittingPermissao(true);
        try {
            await api({
                method: 'post',
                action: `/eventoRsvp/${id}/permissoes`,
                token,
                data: {
                    participante_ids: usuarioPermissaoSelecionado.map((u) => u.value)
                }
            });

            toast.success('Permissão liberada com sucesso.');
            setUsuarioPermissaoSelecionado([]);
            buscar();
        } catch (error) {
            toast.error('Erro ao liberar permissão: ' + (error.response?.data?.error ?? 'Erro desconhecido'));
        } finally {
            setIsSubmittingPermissao(false);
        }
    };

    const removerPermissaoAlteracao = async (participanteId) => {
        setRemovendoPermissaoId(participanteId);
        try {
            await api({
                method: 'delete',
                action: `/eventoRsvp/${id}/permissoes/${participanteId}`,
                token,
            });

            toast.success('Permissão removida com sucesso.');
            buscar();
        } catch (error) {
            toast.error('Erro ao remover permissão: ' + (error.response?.data?.error ?? 'Erro desconhecido'));
        } finally {
            setRemovendoPermissaoId(null);
        }
    };

    const isBlank = (value) => value === null || value === undefined || String(value).trim() === '';

    const validar = () => {
        if ((dados.rsvp ?? 'nao') !== 'sim') {
            return null;
        }

        const rsvp = dados.evento_rsvp_data || {};
        const listaHoteis = rsvp.hotel === 'sim' && Array.isArray(rsvp.lista_hoteis) ? rsvp.lista_hoteis : [];
        if (rsvp.hotel === 'sim' && !aDefinir('hotel') && listaHoteis.length === 0) {
            return 'Adicione ao menos um hotel';
        }

        if (!(aDefinir("controle_cotas")||naoSeAplica('controle_cotas')) && !dados.evento_rsvp_data?.arquivo_controle_cotas?.id && !dados.evento_rsvp_data?.filecontrolecotas) {
            return 'Arquivo Controle de Cotas é obrigatório';
        }

        if (!aDefinir('hotel') && listaHoteis.length > 0) {
            for (let index = 0; index < listaHoteis.length; index++) {
                const item = listaHoteis[index];
                const hotelPreenchido = eventoInternacional
                    ? !isBlank(item?.nomeHotel ?? item?.nome_hotel)
                    : !isBlank(item?.hotel);
                if (
                    !hotelPreenchido ||
                    isBlank(item?.data_checkin) ||
                    isBlank(item?.hora_checkin) ||
                    isBlank(item?.data_checkout) ||
                    isBlank(item?.hora_checkout) ||
                    isBlank(item?.quantidade_pessoas) ||
                    (colunasHotelAtivas.single && isBlank(item?.bloqueados_single)) ||
                    (colunasHotelAtivas.double && isBlank(item?.bloqueados_double))
                ) {
                    return 'Preencha os hotéis corretamente';
                }
            }
        }

        return null;
    };

    // Obrigatórios: travam o Start (sem bypass).
    const obterCamposEventoObrigatoriosFaltantes = () => {
        return CAMPOS_EVENTO_START
            .filter((campo) => CAMPOS_EVENTO_OBRIGATORIOS_START.includes(campo.key))
            .filter((campo) => !campo.quando || campo.quando(dados))
            .filter((campo) => !valorPreenchidoEvento(dados[campo.key]))
            .map((campo) => campo.label);
    };

    // Opcionais: só aviso (podem ser ignorados com "Sim, enviar Start").
    const obterCamposEventoFaltantes = () => {
        return CAMPOS_EVENTO_START
            .filter((campo) => !CAMPOS_EVENTO_OBRIGATORIOS_START.includes(campo.key))
            .filter((campo) => !campo.quando || campo.quando(dados))
            .filter((campo) => !valorPreenchidoEvento(dados[campo.key]))
            .map((campo) => campo.label);
    };

    const salvar = () => {
        if (!podeSalvar) {
            toast.error('Não há campos disponíveis para edição.');
            return;
        }

        const erro = validar();
        if (erro) {
            toast.error(erro);
            return;
        }

        // Antes de disparar o Start de RSVP (ativação pelo operador), avisa quais campos do evento
        // (abas Geral/Detalhes) estão vazios e pede confirmação para enviar mesmo assim.
        const vaiDispararStart = (dados.rsvp ?? 'nao') === 'sim' && !startRsvpEnviado;
        if (vaiDispararStart) {
            const obrigatorios = obterCamposEventoObrigatoriosFaltantes();
            const opcionais = obterCamposEventoFaltantes();
            if (obrigatorios.length > 0 || opcionais.length > 0) {
                setCamposEventoObrigatoriosFaltantes(obrigatorios);
                setCamposEventoFaltantes(opcionais);
                setShowModalCamposEvento(true);
                return;
            }
        }

        prosseguirSalvar();
    };

    const prosseguirSalvar = () => {
        setShowModalCamposEvento(false);
        setIsSubmitting(true);

        // Centralizado no POST /eventoRsvp: ele salva os dados do RSVP, atualiza as flags
        // rsvp/visibilidade_rsvp do evento e dispara os e-mails de Visibilidade/Start na ordem
        // correta (já com os dados frescos). O PUT /evento não toca mais nesses campos.
        Promise.resolve()
            .then(function () {
                // Preparar dados do RSVP para salvar
                const dadosRsvp = { ...dados.evento_rsvp_data };
                dadosRsvp.questionar_restricao_alimentar = dadosRsvp.questionar_restricao_alimentar === 'sim' ? 'sim' : 'nao';
                dadosRsvp.questionar_membro_associacao = dadosRsvp.questionar_membro_associacao === 'sim' ? 'sim' : 'nao';
                dadosRsvp.questionar_necessidade_mobilidade = dadosRsvp.questionar_necessidade_mobilidade === 'sim' ? 'sim' : 'nao';
                dadosRsvp.questionar_nome_cracha = dadosRsvp.questionar_nome_cracha === 'sim' ? 'sim' : 'nao';

                // Converter datas para formato correto se necessário ou garantir que são enviadas
                if (dadosRsvp.previsao_recebimento_listas) {
                    const dataConvertida = dataMysql(dadosRsvp.previsao_recebimento_listas);
                    // Validar se a data é válida (não é "Invalid date")
                    if (dataConvertida && dataConvertida !== 'Invalid date' && moment(dataConvertida, 'YYYY-MM-DD').isValid()) {
                        dadosRsvp.previsao_recebimento_listas = dataConvertida;
                    } else {
                        // Se a data for inválida, remover o campo
                        delete dadosRsvp.previsao_recebimento_listas;
                    }
                }

                // Limpar campos que não devem ser enviados (relacionamentos e campos calculados)
                const camposParaRemover = [
                    'planilha_arquivo',
                    'malha_aerea_arquivo',
                    'agenda_previa_arquivo',
                    'arquivo_controle_cotas',
                    'documentos_diversos_arquivos',
                    'data_hospedagem',
                    'data_saida',
                    'nomeHotel',
                    'id',
                    'created_at',
                    'updated_at',
                    'quantidade_apartamentos_previsto',
                    'pacote_inscricao_quantidade', // espelho de tela do switch — a coluna real é numero_inscricoes
                ];

                camposParaRemover.forEach(campo => {
                    delete dadosRsvp[campo];
                });

                const camposInteiros = [
                    'quantidade_hcp', 'speakers_confirmados',
                    'pacote_aereo_quantidade', 'pacote_hospedagem_quantidade',
                    'pacote_transfer_quantidade', 'numero_inscricoes',
                    'pacote_completo_quantidade', 'numero_hospedagens',
                ];
                camposInteiros.forEach(campo => {
                    if (dadosRsvp[campo] !== null && dadosRsvp[campo] !== undefined && dadosRsvp[campo] !== '') {
                        dadosRsvp[campo] = Number(dadosRsvp[campo]);
                    }
                });

                // Lista de hotéis: enviar apenas itens com pelo menos um preenchimento (hotel | check-in | check-out | qtd)
                if (dadosRsvp.lista_hoteis && Array.isArray(dadosRsvp.lista_hoteis)) {
                    if (aDefinir('hotel')) {
                        dadosRsvp.lista_hoteis = [];
                    } else {
                        dadosRsvp.lista_hoteis = dadosRsvp.lista_hoteis
                            .map(item => ({
                                hotel: eventoInternacional ? null : (item.hotel || null),
                                nome_hotel: item.nomeHotel || item.nome_hotel || null,
                                data_checkin: item.data_checkin || null,
                                hora_checkin: item.hora_checkin || null,
                                data_checkout: item.data_checkout || null,
                                hora_checkout: item.hora_checkout || null,
                                quantidade_pessoas: item.quantidade_pessoas !== '' && item.quantidade_pessoas !== undefined && item.quantidade_pessoas !== null ? Number(item.quantidade_pessoas) : null,
                                ...(colunasHotelAtivas.single ? { bloqueados_single: Number.isFinite(Number(item.bloqueados_single)) ? Number(item.bloqueados_single) : null } : {}),
                                ...(colunasHotelAtivas.double ? { bloqueados_double: Number.isFinite(Number(item.bloqueados_double)) ? Number(item.bloqueados_double) : null } : {}),
                            }))
                            .filter(item => item.hotel || item.nome_hotel || item.data_checkin || item.hora_checkin || item.data_checkout || item.hora_checkout || (item.quantidade_pessoas != null && item.quantidade_pessoas > 0));
                    }
                }

                if (dadosRsvp.observacoes_campos && typeof dadosRsvp.observacoes_campos === 'object') {
                    const quantidadeHoteis = Array.isArray(dadosRsvp.lista_hoteis) ? dadosRsvp.lista_hoteis.length : 0;
                    dadosRsvp.observacoes_campos = Object.entries(dadosRsvp.observacoes_campos).reduce((acc, [campo, observacao]) => {
                        const texto = String(observacao || '').trim();
                        if (!texto) {
                            return acc;
                        }

                        const matchHotel = campo.match(/^lista_hoteis\.(\d+)\./);
                        if (matchHotel && Number(matchHotel[1]) >= quantidadeHoteis) {
                            return acc;
                        }

                        acc[campo] = texto;
                        return acc;
                    }, {});

                    if (Object.keys(dadosRsvp.observacoes_campos).length === 0) {
                        dadosRsvp.observacoes_campos = null;
                    }
                }

                // Apagar um campo precisa PERSISTIR o vazio. Deletar a chave sumia do payload e o back
                // mantinha o valor antigo (erase não aplicava). Mantemos o '' no payload (só tiramos
                // undefined): a string vazia sobrevive tanto ao JSON quanto ao multipart (null seria
                // descartado por appendToFormData), e o ConvertEmptyStringsToNull do Laravel a
                // normaliza para null no servidor — limpando o campo nos dois caminhos.
                Object.keys(dadosRsvp).forEach(key => {
                    if (dadosRsvp[key] === undefined) {
                        delete dadosRsvp[key];
                    }
                });

                return api({
                    method: 'post',
                    action: '/eventoRsvp',
                    token: token,
                    data: {
                        evento_id: id,
                        rsvp: dados.rsvp,
                        visibilidade_rsvp: dados.visibilidade_rsvp ?? 'nao',
                        ...dadosRsvp
                    }
                });
            })
            .then(function () {
                // Salvou no servidor: o rascunho local cumpriu o papel e pode ser descartado.
                podeAutoSalvarRef.current = false;
                if (rascunhoStore) rascunhoStore.limpar();
                toast.success("Dados salvos com sucesso!");
                history.push("/evento?idEvento=" + id);
            })
            .catch(function (error) {
                const errorMessage = error.response?.data?.error ?? "Erro desconhecido";
                const errorDetails = error.response?.data?.details;
                if (errorDetails) {
                    const detailsText = Object.entries(errorDetails).map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`).join('; ');
                    toast.error("Erro ao salvar dados: " + errorMessage + " (" + detailsText + ")");
                } else {
                    toast.error("Erro ao salvar dados: " + errorMessage);
                }
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    useEffect(() => {
        const quantidadeHcp = parseInt(dados?.evento_rsvp_data?.quantidade_hcp || 0);
        const speakersConfirmados = parseInt(dados?.evento_rsvp_data?.speakers_confirmados || 0);
        const total = quantidadeHcp + speakersConfirmados;
        setDados({ ...dados, quantidadePessoas: total });
    }, [
        dados?.evento_rsvp_data?.quantidade_hcp,
        dados?.evento_rsvp_data?.speakers_confirmados
    ]);

    // Arma o auto-save depois que o operador decide sobre um rascunho pendente. Captura os anexos
    // atuais como baseline (para o diff) antes de liberar as gravações. Roda após o estado assentar.
    useEffect(() => {
        if (!autoSaveArmado) return;
        ultimoSalvoRef.current = coletarArquivos();
        ultimoCamposRef.current = chaveCampos(montarCampos());
        podeAutoSalvarRef.current = true;
        // eslint-disable-next-line
    }, [autoSaveArmado]);

    // Auto-save do rascunho a cada alteração (sem debounce). Anexos: grava/remove só o que mudou,
    // um arquivo por vez (nenhum outro blob é tocado). Texto/aux: uma entrada só, barata (sem blobs).
    useEffect(() => {
        if (!podeAutoSalvarRef.current || !rascunhoStore) return;
        const anterior = ultimoSalvoRef.current || {};
        const atual = coletarArquivos();
        ARQUIVO_CAMPOS.forEach((campo) => {
            if (atual[campo] !== anterior[campo]) {
                if (atual[campo]) rascunhoStore.salvarArquivo(campo, atual[campo]);
                else rascunhoStore.removerArquivo(campo);
            }
        });
        // Só grava o rascunho de texto se ele REALMENTE mudou. Sem isso, qualquer buscar() (ex.: após
        // gerir permissões) reescrevia um rascunho igual ao servidor e disparava o modal na próxima
        // abertura. salvoEm fica fora da comparação por ser regenerado a cada montarCampos().
        const campos = montarCampos();
        const chave = chaveCampos(campos);
        if (chave !== ultimoCamposRef.current) {
            rascunhoStore.salvarCampos(campos);
            ultimoCamposRef.current = chave;
        }
        ultimoSalvoRef.current = atual;
        // eslint-disable-next-line
    }, [dados, arquivosSalvos, naoSeAplicaState, aDefinirState, colunasHotelAtivas, agendaPreviaMode]);

    return (
        <ErpTemplate processando={isLoading}>
            <div className="main-content container-fluid">
                <div className="page-title">
                    <h3>Dados Iniciais RSVP</h3>
                </div>
                <section id="multiple-column-form">
                    <div className="row match-height">
                        <div className="col-md-12">
                            <div className="card">
                                <div className="card-content">
                                    <div className="card-body">
                                        <form className="form" onSubmit={(e) => { e.preventDefault(); salvar(); }}>

                                            <div className="alert alert-info">
                                                Caso não haja necessidade, preencha os campos obrigatórios com "N/A".
                                            </div>

                                            {possuiCampoPreenchidoTravado && (
                                                <div className="alert alert-warning">
                                                    Campos já preenchidos só podem ser alterados com liberação da gerente de RSVP.
                                                </div>
                                            )}

                                            {podeGerenciarPermissoes && existeRegistroRsvp && (
                                                <div className="card mb-4">
                                                    <div className="card-header">
                                                        <h5 className="mb-0">👤 Permissões de Alteração</h5>
                                                    </div>
                                                    <div className="card-body">
                                                        <div className="row align-items-end">
                                                            <div className="col-md-8">
                                                                <div className="form-group">
                                                                    <label>Liberar usuário</label>
                                                                    <ReactSelect
                                                                        value={usuarioPermissaoSelecionado}
                                                                        onChange={setUsuarioPermissaoSelecionado}
                                                                        options={opcoesUsuariosPermissao}
                                                                        placeholder="Selecione..."
                                                                        isMulti
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="col-md-4">
                                                                <div className="form-group">
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-primary w-100"
                                                                        onClick={adicionarPermissaoAlteracao}
                                                                        disabled={isSubmittingPermissao}
                                                                    >
                                                                        {isSubmittingPermissao ? 'Liberando...' : 'Liberar alteração'}
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="mt-3">
                                                            <label>Usuários liberados</label>
                                                            {permissoesAtivas.length === 0 ? (
                                                                <div className="text-muted">Nenhum usuário liberado.</div>
                                                            ) : (
                                                                permissoesAtivas.map((participante) => (
                                                                    <div key={participante.id} className="d-flex align-items-center justify-content-between border rounded p-2 mb-2">
                                                                        <span>{participante.nome}</span>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-outline-danger"
                                                                            onClick={() => removerPermissaoAlteracao(participante.id)}
                                                                            disabled={removendoPermissaoId === participante.id}
                                                                        >
                                                                            <FaTrash /> {removendoPermissaoId === participante.id ? 'Revogando...' : 'Revogar'}
                                                                        </button>
                                                                    </div>
                                                                ))
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {(dados.rsvp ?? 'nao') === 'sim' && (dados.visibilidade_rsvp ?? 'nao') !== 'sim' ? (
                                                <div className="alert alert-danger">
                                                    Este evento não teve a visibilidade do RSVP ativada e vai entrar fora do planejamento.
                                                </div>
                                            ) : ehAbbvie && (dados.rsvp ?? 'nao') === 'sim' && (
                                                <div className="alert alert-warning">
                                                    Evento Abbvie: Start de RSVP será disparado automaticamente quando uma P.O. for aprovada.
                                                </div>
                                            )}

                                            <div className="card mb-4">
                                                <div className="card-header">
                                                    <div className="d-flex align-items-center justify-content-between">
                                                        <h5 className="mb-0">📋 Configurações de RSVP</h5>
                                                        <div className="d-flex align-items-center gap-3">
                                                            {/* Data do Start ao lado dos toggles, mesmo padrão do "Abertura enviada em"
                                                                da abertura de backoffice (CadastroEvento) — o Start é irreversível e
                                                                a tela não mostrava quando ele saiu. */}
                                                            {startRsvpEnviado && (
                                                                <small className="text-muted">
                                                                    Start enviado em {moment(dados.evento_rsvp_data.started_at).format('DD/MM/YYYY HH:mm')}
                                                                </small>
                                                            )}
                                                            <div className="form-check form-switch mb-0">
                                                                <input
                                                                    className="form-check-input"
                                                                    type="checkbox"
                                                                    checked={(dados.visibilidade_rsvp ?? 'nao') === 'sim'}
                                                                    disabled={(dadosOriginais?.visibilidade_rsvp ?? 'nao') === 'sim'}
                                                                    title={(dadosOriginais?.visibilidade_rsvp ?? 'nao') === 'sim' ? 'A visibilidade não pode ser desativada após ativada.' : undefined}
                                                                    onChange={(e) => {
                                                                        if (!e.target.checked && dados.rsvp !=="sim" && visibilidadeTemCampoPreenchido()) {
                                                                            toast.warning('Limpe os campos da visibilidade antes de desativar.');
                                                                            return;
                                                                        }
                                                                        setDados({ ...dados, visibilidade_rsvp: e.target.checked ? 'sim' : 'nao' });
                                                                    }}
                                                                />
                                                                <label className="form-check-label">Visibilidade</label>
                                                            </div>
                                                            <div className="form-check form-switch mb-0">
                                                                <input
                                                                    className="form-check-input"
                                                                    type="checkbox"
                                                                    checked={(dados.rsvp ?? 'nao') === 'sim'}
                                                                    disabled={(dadosOriginais?.rsvp ?? 'nao') === 'sim'}
                                                                    title={(dadosOriginais?.rsvp ?? 'nao') === 'sim' ? 'O RSVP não pode ser desativado após ativado.' : undefined}
                                                                    onChange={(e) => {
                                                                        if (!e.target.checked && rsvpTemCampoPreenchido()) {
                                                                            toast.warning('Limpe os campos do RSVP antes de desativar.');
                                                                            return;
                                                                        }
                                                                        if (e.target.checked && dados.localNaoDefinido) {
                                                                            setShowModalRsvpSemLocal(true);
                                                                            return;
                                                                        }
                                                                        setDados({ ...dados, rsvp: e.target.checked ? 'sim' : 'nao' });
                                                                    }}
                                                                />
                                                                <label className="form-check-label">Ativar RSVP</label>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="card-body">
                                                    <div className="row mb-3">
                                                        <div className="col-md-6">
                                                            <div className="form-group">
                                                                {labelObservacao('responsavel_subir_participantes', <>Responsável por Subir Participantes no Sistema do cliente {obrigatorio}</>)}
                                                                <select
                                                                    required
                                                                    className="form-select"
                                                                    value={dados.evento_rsvp_data?.responsavel_subir_participantes || ''}
                                                                    disabled={!campoPodeEditar('responsavel_subir_participantes')}
                                                                    onChange={(e) => {
                                                                        const rsvp = dados.evento_rsvp_data || {};
                                                                        const valor = e.target.value;
                                                                        rsvp.responsavel_subir_participantes = valor !== '' ? valor : null;
                                                                        setDados({ ...dados, evento_rsvp_data: rsvp });
                                                                    }}
                                                                >
                                                                    <option value="" hidden disabled>Selecione...</option>
                                                                    <option value="backoffice">BackOffice</option>
                                                                    <option value="cliente">Cliente</option>
                                                                </select>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="row mb-2">
                                                        <div className="col-md-6">
                                                            <div className="form-group">
                                                                <div className="d-flex align-items-center justify-content-between">
                                                                    {labelObservacao('planilha_convidados', <>Planilha de Convidados {obrigatorio}</>)}
                                                                    <div className="d-flex align-items-center">
                                                                        <div className="form-check ms-2 mb-0">
                                                                            <input
                                                                                type="checkbox"
                                                                                className="form-check-input"
                                                                                id="planilhaConstaCliente"
                                                                                checked={!!dados.evento_rsvp_data?.planilha_consta_cliente}
                                                                                disabled={!!dados.evento_rsvp_data?.planilha_arquivo?.nome || !campoPodeEditar('planilha_convidados')}
                                                                                onChange={(e) => atualizarFlagExclusiva('planilha_convidados', 'planilha_consta_cliente', e.target.checked)}
                                                                            />
                                                                            <label className="form-check-label mb-0" htmlFor="planilhaConstaCliente">Consta no cliente</label>
                                                                        </div>
                                                                        <div className="form-check ms-2 mb-0">
                                                                            <input
                                                                                type="checkbox"
                                                                                className="form-check-input"
                                                                                id="naoSeAplicaPlanilhaConvidados"
                                                                                checked={naoSeAplica('planilha_convidados')}
                                                                                disabled={!!dados.evento_rsvp_data?.planilha_arquivo?.nome || !campoPodeEditar('planilha_convidados')}
                                                                                onChange={(e) => atualizarNaoSeAplica('planilha_convidados', e.target.checked)}
                                                                            />
                                                                            <label className={`form-check-label mb-0 ${!!dados.evento_rsvp_data?.planilha_arquivo?.nome || !campoPodeEditar('planilha_convidados') ? 'text-muted' : ''}`} htmlFor="naoSeAplicaPlanilhaConvidados">N/A</label>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="d-flex align-items-center">
                                                                    <input
                                                                        ref={planilhaConvidadosInputRef}
                                                                        required={!naoSeAplica('planilha_convidados') && !dados.evento_rsvp_data?.planilha_consta_cliente && !dados.evento_rsvp_data?.planilha_arquivo?.nome && !dados.evento_rsvp_data?.fileplanilha}
                                                                        type="file"
                                                                        className="form-control"
                                                                        disabled={!campoPodeEditar('planilha_convidados') || naoSeAplica('planilha_convidados') || !!dados.evento_rsvp_data?.planilha_consta_cliente}
                                                                        onChange={(e) => {
                                                                            const file = e.target.files[0] || null;
                                                                            setDados({
                                                                                ...dados,
                                                                                evento_rsvp_data: {
                                                                                    ...dados.evento_rsvp_data,
                                                                                    fileplanilha: file,
                                                                                }
                                                                            });
                                                                        }}
                                                                    />
                                                                    {dados.evento_rsvp_data?.planilha_arquivo?.nome && (
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-outline-danger ms-2"
                                                                            onClick={() => {
                                                                                setDados(prev => ({
                                                                                    ...prev,
                                                                                    evento_rsvp_data: {
                                                                                        ...prev.evento_rsvp_data,
                                                                                        planilha_convidados: null,
                                                                                        planilha_arquivo: null,
                                                                                        fileplanilha: null,
                                                                                    }
                                                                                }));
                                                                            }}
                                                                            title="Excluir planilha de convidados"
                                                                            disabled={!campoPodeEditar('planilha_convidados')}
                                                                        >
                                                                            <FaTrash size={16} />
                                                                        </button>
                                                                    )}
                                                                </div>
                                                                {dados.evento_rsvp_data?.planilha_arquivo?.nome && (
                                                                    <div className="mt-2">
                                                                        <small className="text-muted">
                                                                            <strong>Arquivo atual:</strong>
                                                                            <a
                                                                                href={`${process.env.REACT_APP_API_URL}/arquivo/download/${dados.evento_rsvp_data.planilha_arquivo.id}`}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="text-primary text-decoration-none ms-1"
                                                                            >
                                                                                {dados.evento_rsvp_data.planilha_arquivo.nome.split('\\').pop().split('/').pop()}
                                                                            </a>
                                                                        </small>
                                                                    </div>
                                                                )}
                                                                {renderArquivoRascunho('fileplanilha', planilhaConvidadosInputRef)}
                                                            </div>
                                                        </div>
                                                        {naoSeAplica('planilha_convidados') && (
                                                        <div className="col-md-6">
                                                            <div className="form-group">
                                                                <Data
                                                                    labelExterno
                                                                    required={dados.rsvp == "sim" && !naoSeAplica('previsao_recebimento_listas')}
                                                                    label={
                                                                        // <div className="d-flex align-items-center">
                                                                        <>
                                                                            {labelObservacao("previsao_recebimento_listas", <>Previsão de Recebimento das Listas {dados.rsvp == "sim" && obrigatorio}</>)}
                                                                            <div className="form-check ms-2 d-inline-block mb-0">
                                                                                <input
                                                                                    type="checkbox"
                                                                                    className="form-check-input"
                                                                                    id="naoSeAplicaPrevisaoRecebimentoListas"
                                                                                    checked={naoSeAplica('previsao_recebimento_listas')}
                                                                                    disabled={!campoPodeEditar('previsao_recebimento_listas')}
                                                                                    onChange={(e) => atualizarNaoSeAplica('previsao_recebimento_listas', e.target.checked)}
                                                                                />
                                                                                <label className="form-check-label mb-0" htmlFor="naoSeAplicaPrevisaoRecebimentoListas">N/A</label>
                                                                            </div>
                                                                        </>
                                                                        // </div>
                                                                    }
                                                                    value={dados.evento_rsvp_data?.previsao_recebimento_listas}
                                                                    disabled={!campoPodeEditar('previsao_recebimento_listas') || naoSeAplica('previsao_recebimento_listas')}
                                                                    onChange={date => {
                                                                        const rsvp = dados.evento_rsvp_data || {};
                                                                        rsvp.previsao_recebimento_listas = date;
                                                                        setDados({ ...dados, evento_rsvp_data: rsvp });
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                        )}
                                                    </div>
                                                    {((dados.rsvp ?? 'nao') === 'sim' || (dados.visibilidade_rsvp ?? 'nao') === 'sim') && (
                                                        <CamposVisibilidadeRsvp
                                                            dados={dados}
                                                            setDados={setDados}
                                                            campoPodeEditar={campoPodeEditar}
                                                            switchTravadoLigado={switchTravadoLigado}
                                                            labelObservacao={labelObservacao}
                                                            obrigatorio={obrigatorio}
                                                        />
                                                    )}
                                                    {((dados.rsvp ?? 'nao') === 'sim') ? (<>
                                                        <div className="row">
                                                            <div className="col-md-6">
                                                                <div className="form-group">
                                                                    <div className="d-flex align-items-center justify-content-between">
                                                                        {labelObservacao('malha_aerea', <>Malha Aérea {obrigatorio}</>)}
                                                                        <div className="d-flex align-items-center">
                                                                            <div className="form-check ms-2 mb-0">
                                                                                <input
                                                                                    type="checkbox"
                                                                                    className="form-check-input"
                                                                                    id="malhaAereaSolicitar"
                                                                                    checked={!!dados.evento_rsvp_data?.malha_aerea_solicitar}
                                                                                    disabled={!!dados.evento_rsvp_data?.malha_aerea_arquivo?.nome || !campoPodeEditar('malha_aerea')}
                                                                                    onChange={(e) => atualizarFlagExclusiva('malha_aerea', 'malha_aerea_solicitar', e.target.checked)}
                                                                                />
                                                                                <label className="form-check-label mb-0" htmlFor="malhaAereaSolicitar">Solicitar</label>
                                                                            </div>
                                                                            <div className="form-check ms-2 mb-0">
                                                                                <input
                                                                                    type="checkbox"
                                                                                    className="form-check-input"
                                                                                    id="naoSeAplicaMalhaAerea"
                                                                                    checked={naoSeAplica('malha_aerea')}
                                                                                    disabled={!!dados.evento_rsvp_data?.malha_aerea_arquivo?.nome || !campoPodeEditar('malha_aerea')}
                                                                                    onChange={(e) => atualizarNaoSeAplica('malha_aerea', e.target.checked)}
                                                                                />
                                                                                <label className={`form-check-label mb-0 ${!!dados.evento_rsvp_data?.malha_aerea_arquivo?.nome || !campoPodeEditar('malha_aerea') ? 'text-muted' : ''}`} htmlFor="naoSeAplicaMalhaAerea">N/A</label>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="d-flex align-items-center">
                                                                        <input
                                                                            ref={malhaAereaInputRef}
                                                                            required={!naoSeAplica('malha_aerea') && !dados.evento_rsvp_data?.malha_aerea_solicitar && !dados.evento_rsvp_data?.malha_aerea_arquivo?.nome && !dados.evento_rsvp_data?.filemalhaaerea}
                                                                            type="file"
                                                                            className="form-control"
                                                                            disabled={!campoPodeEditar('malha_aerea') || naoSeAplica('malha_aerea') || !!dados.evento_rsvp_data?.malha_aerea_solicitar}
                                                                            onChange={(e) => {
                                                                                const file = e.target.files[0] || null;
                                                                                setDados({
                                                                                    ...dados,
                                                                                    evento_rsvp_data: {
                                                                                        ...dados.evento_rsvp_data,
                                                                                        filemalhaaerea: file,
                                                                                    }
                                                                                });
                                                                            }}
                                                                        />
                                                                        {dados.evento_rsvp_data?.malha_aerea_arquivo?.nome && (
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-outline-danger ms-2"
                                                                                onClick={() => {
                                                                                    setDados(prev => ({
                                                                                        ...prev,
                                                                                        evento_rsvp_data: {
                                                                                            ...prev.evento_rsvp_data,
                                                                                            malha_aerea: null,
                                                                                            malha_aerea_arquivo: null,
                                                                                            filemalhaaerea: null,
                                                                                        }
                                                                                    }));
                                                                                }}
                                                                                title="Excluir Malha Aérea"
                                                                                disabled={!campoPodeEditar('malha_aerea')}
                                                                            >
                                                                                <FaTrash size={16} />
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                    {dados.evento_rsvp_data?.malha_aerea_arquivo?.nome && (
                                                                        <div className="mt-2">
                                                                            <small className="text-muted">
                                                                                <strong>Arquivo atual:</strong>
                                                                                <a
                                                                                    href={`${process.env.REACT_APP_API_URL}/arquivo/download/${dados.evento_rsvp_data.malha_aerea_arquivo.id}`}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    className="text-primary text-decoration-none ms-1"
                                                                                >
                                                                                    {dados.evento_rsvp_data.malha_aerea_arquivo.nome.split('\\').pop().split('/').pop()}
                                                                                </a>
                                                                            </small>
                                                                        </div>
                                                                    )}
                                                                    {renderArquivoRascunho('filemalhaaerea', malhaAereaInputRef)}
                                                                </div>
                                                            </div>
                                                            <div className='col-md-6'></div>
                                                            <div className="col-md-6">
                                                                <div className="form-group">
                                                                    {labelObservacao('link_inscricao', 'Link de Inscrição')}
                                                                    <input
                                                                        type="text"
                                                                        className="form-control"
                                                                        placeholder="URL do sistema de inscrições"
                                                                        value={dados.evento_rsvp_data?.link_inscricao || ''}
                                                                        disabled={!campoPodeEditar('link_inscricao')}
                                                                        onChange={(e) => {
                                                                            const rsvp = dados.evento_rsvp_data || {};
                                                                            rsvp.link_inscricao = e.target.value;
                                                                            setDados({ ...dados, evento_rsvp_data: rsvp });
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="row">
                                                            <div className="col-md-6">
                                                                <div className="form-group">
                                                                    <div className="form-check form-switch d-flex align-items-center">
                                                                        <input
                                                                            className="form-check-input mt-0"
                                                                            type="checkbox"
                                                                            checked={(dados.evento_rsvp_data?.questionar_restricao_alimentar ?? 'nao') === 'sim'}
                                                                            disabled={switchTravadoLigado('questionar_restricao_alimentar')}
                                                                            title={switchTravadoLigado('questionar_restricao_alimentar') ? 'Não pode ser desativado após o Start.' : undefined}
                                                                            onChange={(e) => {
                                                                                const rsvp = dados.evento_rsvp_data || {};
                                                                                rsvp.questionar_restricao_alimentar = e.target.checked ? 'sim' : 'nao';
                                                                                setDados({ ...dados, evento_rsvp_data: rsvp });
                                                                            }}
                                                                        />
                                                                        {labelObservacao('questionar_restricao_alimentar', 'Questionar sobre restrição alimentar?', { className: 'form-check-label ms-2' })}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="col-md-6">
                                                                <div className="form-group">
                                                                    <div className="form-check form-switch d-flex align-items-center">
                                                                        <input
                                                                            className="form-check-input mt-0"
                                                                            type="checkbox"
                                                                            checked={(dados.evento_rsvp_data?.questionar_membro_associacao ?? 'nao') === 'sim'}
                                                                            disabled={switchTravadoLigado('questionar_membro_associacao')}
                                                                            title={switchTravadoLigado('questionar_membro_associacao') ? 'Não pode ser desativado após o Start.' : undefined}
                                                                            onChange={(e) => {
                                                                                const rsvp = dados.evento_rsvp_data || {};
                                                                                rsvp.questionar_membro_associacao = e.target.checked ? 'sim' : 'nao';
                                                                                setDados({ ...dados, evento_rsvp_data: rsvp });
                                                                            }}
                                                                        />
                                                                        {labelObservacao('questionar_membro_associacao', 'Questionar se o convidado é membro ou sócio da associação/sociedade?', { className: 'form-check-label ms-2' })}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="col-md-6">
                                                                <div className="form-group">
                                                                    <div className="form-check form-switch d-flex align-items-center">
                                                                        <input
                                                                            className="form-check-input mt-0"
                                                                            type="checkbox"
                                                                            checked={(dados.evento_rsvp_data?.questionar_necessidade_mobilidade ?? 'nao') === 'sim'}
                                                                            disabled={switchTravadoLigado('questionar_necessidade_mobilidade')}
                                                                            title={switchTravadoLigado('questionar_necessidade_mobilidade') ? 'Não pode ser desativado após o Start.' : undefined}
                                                                            onChange={(e) => {
                                                                                const rsvp = dados.evento_rsvp_data || {};
                                                                                rsvp.questionar_necessidade_mobilidade = e.target.checked ? 'sim' : 'nao';
                                                                                setDados({ ...dados, evento_rsvp_data: rsvp });
                                                                            }}
                                                                        />
                                                                        {labelObservacao('questionar_necessidade_mobilidade', 'Questionar se limitação de mobilidade?', { className: 'form-check-label ms-2' })}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="col-md-6">
                                                                <div className="form-group">
                                                                    <div className="form-check form-switch d-flex align-items-center">
                                                                        <input
                                                                            className="form-check-input mt-0"
                                                                            type="checkbox"
                                                                            checked={(dados.evento_rsvp_data?.questionar_nome_cracha ?? 'nao') === 'sim'}
                                                                            disabled={switchTravadoLigado('questionar_nome_cracha')}
                                                                            title={switchTravadoLigado('questionar_nome_cracha') ? 'Não pode ser desativado após o Start.' : undefined}
                                                                            onChange={(e) => {
                                                                                const rsvp = dados.evento_rsvp_data || {};
                                                                                rsvp.questionar_nome_cracha = e.target.checked ? 'sim' : 'nao';
                                                                                setDados({ ...dados, evento_rsvp_data: rsvp });
                                                                            }}
                                                                        />
                                                                        {labelObservacao('questionar_nome_cracha', 'Questionar nome para crachá?', { className: 'form-check-label ms-2' })}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="row">
                                                            <div className="col-md-12">
                                                                <div className="form-group">
                                                                    {labelObservacao('link_quantidade_inscricao_prazo_sociedade', <>Prazo da Sociedade/Associação {obrigatorio}</>)}
                                                                    <textarea
                                                                        className="form-control"
                                                                        rows="3"
                                                                        placeholder="Informações sobre inscrições e prazos da sociedade organizadora"
                                                                        required
                                                                        value={dados.evento_rsvp_data?.link_quantidade_inscricao_prazo_sociedade || ''}
                                                                        disabled={!campoPodeEditar('link_quantidade_inscricao_prazo_sociedade')}
                                                                        onChange={(e) => {
                                                                            const rsvp = dados.evento_rsvp_data || {};
                                                                            rsvp.link_quantidade_inscricao_prazo_sociedade = e.target.value;
                                                                            setDados({ ...dados, evento_rsvp_data: rsvp });
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="row">
                                                            <div className="col-md-6">
                                                                <div className="form-group">
                                                                    {labelObservacao('quantidade_prevista_aereo_valor_aprovado', <>Valor Aprovado de Aéreo {obrigatorio}</>)}
                                                                    <input
                                                                        type="text"
                                                                        className="form-control"
                                                                        placeholder="Ex: 15 passagens / Ticket médio R$ 2.500,00"
                                                                        required
                                                                        value={dados.evento_rsvp_data?.quantidade_prevista_aereo_valor_aprovado || ''}
                                                                        disabled={!campoPodeEditar('quantidade_prevista_aereo_valor_aprovado')}
                                                                        onChange={(e) => {
                                                                            const rsvp = dados.evento_rsvp_data || {};
                                                                            rsvp.quantidade_prevista_aereo_valor_aprovado = e.target.value;
                                                                            setDados({ ...dados, evento_rsvp_data: rsvp });
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                            {eventoInternacional && (<>
                                                            <div className="col-md-6">
                                                                <div className="form-group">
                                                                    {labelObservacao('classevoo', <>Classe do Voo {obrigatorio}</>)}
                                                                    <select
                                                                        required
                                                                        className="form-control"
                                                                        value={dados.evento_rsvp_data?.classevoo ?? ''}
                                                                        disabled={!campoPodeEditar('classevoo')}
                                                                        onChange={(e) => {
                                                                            const rsvp = dados.evento_rsvp_data || {};
                                                                            rsvp.classevoo = e.target.value || null;
                                                                            setDados({ ...dados, evento_rsvp_data: rsvp });
                                                                        }}
                                                                    >
                                                                        <option value="" hidden>Selecione...</option>
                                                                        {classeVooOptions.map((option) => (
                                                                            <option key={option.value} value={option.value}>
                                                                                {option.label}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                            </div>
                                                            <div className="col-md-6 order-md-3">
                                                                <div className="form-group">
                                                                    {labelObservacao('reembolso_aereo', 'Bilhete com Reembolso')}
                                                                    <select
                                                                        className="form-control"
                                                                        value={dados.evento_rsvp_data?.reembolso_aereo || ''}
                                                                        disabled={!campoPodeEditar('reembolso_aereo')}
                                                                        onChange={(e) => {
                                                                            const rsvp = dados.evento_rsvp_data || {};
                                                                            rsvp.reembolso_aereo = e.target.value;
                                                                            setDados({ ...dados, evento_rsvp_data: rsvp });
                                                                        }}
                                                                    >
                                                                        <option value="" hidden>Selecione...</option>
                                                                        <option value="sim">Sim</option>
                                                                        <option value="nao">Não</option>
                                                                    </select>
                                                                </div>
                                                            </div>
                                                            <div className="col-md-6 order-md-2">
                                                                <div className="form-group">
                                                                    {labelObservacao('seguro_viagem', 'Considerar seguro viagem?')}
                                                                    <select
                                                                        className="form-control"
                                                                        value={dados.evento_rsvp_data?.seguro_viagem || ''}
                                                                        disabled={!campoPodeEditar('seguro_viagem')}
                                                                        onChange={(e) => {
                                                                            const rsvp = dados.evento_rsvp_data || {};
                                                                            rsvp.seguro_viagem = e.target.value;
                                                                            setDados({ ...dados, evento_rsvp_data: rsvp });
                                                                        }}
                                                                    >
                                                                        <option value="" hidden>Selecione...</option>
                                                                        <option value="sim">Sim</option>
                                                                        <option value="nao">Não</option>
                                                                    </select>
                                                                </div>
                                                            </div>
                                                            </>)}
                                                        </div>
                                                        <div className="row">
                                                            <div className="col-md-12">
                                                                <div className="form-group">
                                                                    {labelObservacao('observacoes_aereo', 'Observações de Aéreo')}
                                                                    <textarea
                                                                        className="form-control"
                                                                        rows="3"
                                                                        placeholder="Informe observações sobre aéreo"
                                                                        value={dados.evento_rsvp_data?.observacoes_aereo || ''}
                                                                        disabled={!campoPodeEditar('observacoes_aereo')}
                                                                        onChange={(e) => {
                                                                            const rsvp = dados.evento_rsvp_data || {};
                                                                            rsvp.observacoes_aereo = e.target.value;
                                                                            setDados({ ...dados, evento_rsvp_data: rsvp });
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="row">
                                                            <div className="col-md-12">
                                                                <div className="form-group">
                                                                    {labelObservacao('informacoes_transfer', <>Informações de Transfer {obrigatorio}</>)}
                                                                    <textarea
                                                                        className="form-control"
                                                                        rows="3"
                                                                        placeholder="Transfer IN/OUT - hotel x evento"
                                                                        required
                                                                        value={dados.evento_rsvp_data?.informacoes_transfer || ''}
                                                                        disabled={!campoPodeEditar('informacoes_transfer')}
                                                                        onChange={(e) => {
                                                                            const rsvp = dados.evento_rsvp_data || {};
                                                                            rsvp.informacoes_transfer = e.target.value;
                                                                            setDados({ ...dados, evento_rsvp_data: rsvp });
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="row">
                                                            <div className="col-md-12">
                                                                <div className="form-group">
                                                                    {labelObservacao('informacoes_inscricoes', 'Informações de Inscrições')}
                                                                    <textarea
                                                                        className="form-control"
                                                                        rows="3"
                                                                        placeholder="Informe orientações sobre inscrições"
                                                                        value={dados.evento_rsvp_data?.informacoes_inscricoes || ''}
                                                                        disabled={!campoPodeEditar('informacoes_inscricoes')}
                                                                        onChange={(e) => {
                                                                            const rsvp = dados.evento_rsvp_data || {};
                                                                            rsvp.informacoes_inscricoes = e.target.value;
                                                                            setDados({ ...dados, evento_rsvp_data: rsvp });
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="row mt-2">
                                                            <div className="col-md-6">
                                                                <div className="form-group">
                                                                    <div className="d-flex justify-content-between align-items-center">
                                                                        {labelObservacao('hotel', <>Possui hotel? {obrigatorio}</>)}
                                                                    </div>
                                                                    <select
                                                                        required
                                                                        className="form-control"
                                                                        value={dados.evento_rsvp_data?.hotel || ''}
                                                                        disabled={!campoPodeEditar('hotel')}
                                                                        onChange={(e) => {
                                                                            const rsvp = { ...(dados.evento_rsvp_data || {}) };
                                                                            rsvp.hotel = e.target.value;
                                                                            setDados({ ...dados, evento_rsvp_data: rsvp });
                                                                        }}
                                                                    >
                                                                        <option value="" hidden>Selecione...</option>
                                                                        <option value="sim">Sim</option>
                                                                        <option value="nao">Não</option>
                                                                    </select>
                                                                </div>

                                                            </div>
                                                            {dados.evento_rsvp_data?.hotel === "sim" && (
                                                                <div className="col-md-6 align-content-center">
                                                                    <div className="form-check">
                                                                        <input
                                                                            type="checkbox"
                                                                            className="form-check-input"
                                                                            id="a_definir_hotel_evento"
                                                                            checked={aDefinir('hotel')}
                                                                            disabled={possuiHotelSelecionado}
                                                                            onChange={(e) => {
                                                                                atualizarADefinir('hotel', e.target.checked);
                                                                            }}
                                                                        />
                                                                        <label className="form-check-label" htmlFor="a_definir_hotel_evento">A definir</label>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                        {possuiHotel && !aDefinir('hotel') && (
                                                            <div className="row mt-2">
                                                                <div className="col-12">
                                                                    <div className="form-group">
                                                                        <label>Lista de hotéis</label>
                                                                        <div className="table-responsive">
                                                                            <table className="table table-sm table-bordered">
                                                                                <thead>
                                                                                    <tr>
                                                                                        <th>Hotel</th>
                                                                                        <th>Data check-in</th>
                                                                                        <th>Hora check-in</th>
                                                                                        <th>Data check-out</th>
                                                                                        <th>Hora check-out</th>
                                                                                        <th>Quantidade de pessoas</th>
                                                                                        {colunasHotelAtivas.single && (
                                                                                            <th>
                                                                                                <div className="d-flex justify-content-between align-items-center">
                                                                                                    <span>Bloqueados (Single)</span>
                                                                                                    {(dados.evento_rsvp_data?.lista_hoteis || []).every(h => h.bloqueados_single === '' || h.bloqueados_single == null) && (
                                                                                                        <button type="button" className="btn btn-sm btn-outline-danger p-0 px-1" onClick={() => removerColunaHotel('single')}>
                                                                                                            <FaTrash size={10} />
                                                                                                        </button>
                                                                                                    )}
                                                                                                </div>
                                                                                            </th>
                                                                                        )}
                                                                                        {colunasHotelAtivas.double && (
                                                                                            <th>
                                                                                                <div className="d-flex justify-content-between align-items-center">
                                                                                                    <span>Bloqueados (Double)</span>
                                                                                                    {(dados.evento_rsvp_data?.lista_hoteis || []).every(h => h.bloqueados_double === '' || h.bloqueados_double == null) && (
                                                                                                        <button type="button" className="btn btn-sm btn-outline-danger p-0 px-1" onClick={() => removerColunaHotel('double')}>
                                                                                                            <FaTrash size={10} />
                                                                                                        </button>
                                                                                                    )}
                                                                                                </div>
                                                                                            </th>
                                                                                        )}
                                                                                        {(!colunasHotelAtivas.single || !colunasHotelAtivas.double) && (
                                                                                            <th style={{ width: '1px', whiteSpace: 'nowrap' }}>
                                                                                                <div className='d-flex gap-3 align-items-center' style={{width:"fit-content"}}>
                                                                                                Quartos Bloqueados
                                                                                                {!colunasHotelAtivas.single && !colunasHotelAtivas.double ? (
                                                                                                    <select
                                                                                                        title="Bloqueados (Single/Double)"
                                                                                                        className="btn btn-primary btn-sm"
                                                                                                        style={{ appearance: 'none', WebkitAppearance: 'none', cursor: 'pointer' }}
                                                                                                        value=""
                                                                                                        onChange={e => { if (e.target.value) adicionarColunaHotel(e.target.value); }}
                                                                                                    >
                                                                                                        <option value="" hidden>+</option>
                                                                                                        <option value="single" className='bg-white text-dark'>Single</option>
                                                                                                        <option value="double" className='bg-white text-dark'>Double</option>
                                                                                                    </select>
                                                                                                ) : (
                                                                                                    <button
                                                                                                        style={{width:"56px"}}
                                                                                                        title={`Bloqueados (${colunasHotelAtivas.single ? 'Double' : 'Single'})`}
                                                                                                        type="button"
                                                                                                        className="btn btn-primary btn-sm"
                                                                                                        onClick={() => adicionarColunaHotel(colunasHotelAtivas.single ? 'double' : 'single')}
                                                                                                    >+</button>
                                                                                                )}
                                                                                                </div>
                                                                                            </th>
                                                                                        )}
                                                                                        <th style={{ width: '50px' }}></th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {(dados.evento_rsvp_data?.lista_hoteis || []).map((item, index) => (
                                                                                        <tr key={index}>
                                                                                            <td style={{ minWidth: '220px' }}>
                                                                                                {eventoInternacional ? (
                                                                                                    <input
                                                                                                        type="text"
                                                                                                        className="form-control form-control-sm"
                                                                                                        placeholder="Digite o nome do hotel"
                                                                                                        value={item.nomeHotel || item.nome_hotel || ''}
                                                                                                        disabled={!campoPodeEditar(`lista_hoteis[${index}].nomeHotel`)}
                                                                                                        onChange={(e) => {
                                                                                                            atualizarLinhaHotel(index, (linhaAtual) => criarLinhaHotel({
                                                                                                                ...linhaAtual,
                                                                                                                hotel: '',
                                                                                                                nomeHotel: e.target.value,
                                                                                                            }));
                                                                                                        }}
                                                                                                    />
                                                                                                ) : (
                                                                                                    <ReactSelect
                                                                                                        value={item.hotel ? { value: item.hotel, label: item.nomeHotel || 'Hotel' } : null}
                                                                                                        readOnly={!campoPodeEditar(`lista_hoteis[${index}].hotel`)}
                                                                                                        placeholder="Selecione..."
                                                                                                        onChange={e => {
                                                                                                            atualizarLinhaHotel(index, (linhaAtual) => {
                                                                                                                if (!e) {
                                                                                                                    return criarLinhaHotel({
                                                                                                                        ...linhaAtual,
                                                                                                                        hotel: '',
                                                                                                                        nomeHotel: '',
                                                                                                                    });
                                                                                                                }

                                                                                                                return criarLinhaHotel({
                                                                                                                    ...linhaAtual,
                                                                                                                    hotel: e.value,
                                                                                                                    nomeHotel: e.label,
                                                                                                                });
                                                                                                            });
                                                                                                        }}
                                                                                                        loadOptions={carregarOpcoesHotel}
                                                                                                    />
                                                                                                )}
                                                                                            </td>
                                                                                            <td>
                                                                                                <input
                                                                                                    type="date"
                                                                                                    className="form-control form-control-sm"
                                                                                                    value={item.data_checkin || ''}
                                                                                                    disabled={!campoPodeEditar(`lista_hoteis[${index}].data_checkin`)}
                                                                                                    onChange={(e) => {
                                                                                                        atualizarLinhaHotel(index, (linhaAtual) => ({
                                                                                                            ...linhaAtual,
                                                                                                            data_checkin: e.target.value,
                                                                                                        }));
                                                                                                    }}
                                                                                                />
                                                                                            </td>
                                                                                            <td>
                                                                                                <input
                                                                                                    type="time"
                                                                                                    className="form-control form-control-sm"
                                                                                                    value={item.hora_checkin || ''}
                                                                                                    disabled={!campoPodeEditar(`lista_hoteis[${index}].hora_checkin`)}
                                                                                                    onChange={(e) => {
                                                                                                        atualizarLinhaHotel(index, (linhaAtual) => ({
                                                                                                            ...linhaAtual,
                                                                                                            hora_checkin: e.target.value,
                                                                                                        }));
                                                                                                    }}
                                                                                                />
                                                                                            </td>
                                                                                            <td>
                                                                                                <input
                                                                                                    type="date"
                                                                                                    className="form-control form-control-sm"
                                                                                                    value={item.data_checkout || ''}
                                                                                                    disabled={!campoPodeEditar(`lista_hoteis[${index}].data_checkout`)}
                                                                                                    onChange={(e) => {
                                                                                                        atualizarLinhaHotel(index, (linhaAtual) => ({
                                                                                                            ...linhaAtual,
                                                                                                            data_checkout: e.target.value,
                                                                                                        }));
                                                                                                    }}
                                                                                                />
                                                                                            </td>
                                                                                            <td>
                                                                                                <input
                                                                                                    type="time"
                                                                                                    className="form-control form-control-sm"
                                                                                                    value={item.hora_checkout || ''}
                                                                                                    disabled={!campoPodeEditar(`lista_hoteis[${index}].hora_checkout`)}
                                                                                                    onChange={(e) => {
                                                                                                        atualizarLinhaHotel(index, (linhaAtual) => ({
                                                                                                            ...linhaAtual,
                                                                                                            hora_checkout: e.target.value,
                                                                                                        }));
                                                                                                    }}
                                                                                                />
                                                                                            </td>
                                                                                            <td>
                                                                                                <input
                                                                                                    type="number"
                                                                                                    className="form-control form-control-sm"
                                                                                                    min="0"
                                                                                                    value={item.quantidade_pessoas === '' || item.quantidade_pessoas == null ? '' : item.quantidade_pessoas}
                                                                                                    disabled={!campoPodeEditar(`lista_hoteis[${index}].quantidade_pessoas`)}
                                                                                                    onChange={(e) => {
                                                                                                        const v = e.target.value;
                                                                                                        atualizarLinhaHotel(index, (linhaAtual) => ({
                                                                                                            ...linhaAtual,
                                                                                                            quantidade_pessoas: v === '' ? '' : (parseInt(v, 10) || 0),
                                                                                                        }));
                                                                                                    }}
                                                                                                />
                                                                                            </td>
                                                                                            {colunasHotelAtivas.single && (
                                                                                                <td>
                                                                                                    <input
                                                                                                        type="number"
                                                                                                        className="form-control form-control-sm"
                                                                                                        min={0}
                                                                                                        value={item.bloqueados_single ?? ''}
                                                                                                        disabled={!campoPodeEditar(`lista_hoteis[${index}].bloqueados_single`)}
                                                                                                        onChange={(e) => {
                                                                                                            const v = parseInt(e.target.value, 10);
                                                                                                            atualizarLinhaHotel(index, (linhaAtual) => ({
                                                                                                                ...linhaAtual,
                                                                                                                bloqueados_single: Number.isFinite(v) ? v : '',
                                                                                                            }));
                                                                                                        }}
                                                                                                    />
                                                                                                </td>
                                                                                            )}
                                                                                            {colunasHotelAtivas.double && (
                                                                                                <td>
                                                                                                    <input
                                                                                                        type="number"
                                                                                                        className="form-control form-control-sm"
                                                                                                        min={0}
                                                                                                        value={item.bloqueados_double ?? ''}
                                                                                                        disabled={!campoPodeEditar(`lista_hoteis[${index}].bloqueados_double`)}
                                                                                                        onChange={(e) => {
                                                                                                            const v = parseInt(e.target.value, 10);
                                                                                                            atualizarLinhaHotel(index, (linhaAtual) => ({
                                                                                                                ...linhaAtual,
                                                                                                                bloqueados_double: Number.isFinite(v) ? v : '',
                                                                                                            }));
                                                                                                        }}
                                                                                                    />
                                                                                                </td>
                                                                                            )}
                                                                                            {(!colunasHotelAtivas.single || !colunasHotelAtivas.double) &&<td></td>}
                                                                                            <td>
                                                                                                <button
                                                                                                    type="button"
                                                                                                    className="btn btn-outline-danger btn-sm"
                                                                                                    disabled={!campoPodeEditar('lista_hoteis')}
                                                                                                    onClick={() => removerLinhaHotel(index)}
                                                                                                >
                                                                                                    <FaTrash size={12} />
                                                                                                </button>
                                                                                            </td>
                                                                                        </tr>
                                                                                    ))}
                                                                                </tbody>
                                                                            </table>
                                                                        </div>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-outline-primary btn-sm"
                                                                            disabled={!campoPodeEditar('lista_hoteis')}
                                                                            onClick={() => {
                                                                                atualizarListaHoteis((listaAtual) => [...listaAtual, criarLinhaHotel()]);
                                                                            }}
                                                                        >
                                                                            + Adicionar
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                        <div className="row mt-3">
                                                            <div className="col-md-6">
                                                                <div className="form-group">
                                                                    {labelObservacao('documentos_diversos', 'Documentos Diversos')}
                                                                    <input
                                                                        type="file"
                                                                        multiple
                                                                        className="form-control"
                                                                        onChange={(e) => {
                                                                            // A seleção AGREGA: cada escolha soma à lista já selecionada em vez de
                                                                            // substituí-la (o input file sempre devolve só os arquivos da última
                                                                            // caixa de diálogo). Duplicata = mesmo nome + tamanho + data.
                                                                            const novos = Array.from(e.target.files);
                                                                            e.target.value = ''; // permite reescolher o mesmo arquivo depois de removê-lo
                                                                            setDados((prev) => {
                                                                                const atuais = prev.evento_rsvp_data?.filesdocumentosdiversos || [];
                                                                                const chave = (file) => `${file.name}|${file.size}|${file.lastModified}`;
                                                                                const jaSelecionados = new Set(atuais.map(chave));
                                                                                const lista = [...atuais, ...novos.filter((file) => !jaSelecionados.has(chave(file)))];
                                                                                return {
                                                                                    ...prev,
                                                                                    evento_rsvp_data: {
                                                                                        ...prev.evento_rsvp_data,
                                                                                        filesdocumentosdiversos: lista.length > 0 ? lista : null,
                                                                                    }
                                                                                };
                                                                            });
                                                                        }}
                                                                    />
                                                                    {dados.evento_rsvp_data?.filesdocumentosdiversos?.length > 0 && (
                                                                        <div className="mt-2">
                                                                            <small className="text-muted">
                                                                                Arquivos selecionados:
                                                                                {dados.evento_rsvp_data.filesdocumentosdiversos.map((file, index) => (
                                                                                    <div key={index} className="ms-1 d-flex align-items-center">
                                                                                        <span className="me-2">• {file.name}</span>
                                                                                        <button
                                                                                            type="button"
                                                                                            className="btn btn-sm btn-outline-danger"
                                                                                            title="Remover da seleção"
                                                                                            onClick={() => {
                                                                                                setDados((prev) => {
                                                                                                    const atuais = prev.evento_rsvp_data?.filesdocumentosdiversos || [];
                                                                                                    const lista = atuais.filter((_, i) => i !== index);
                                                                                                    return {
                                                                                                        ...prev,
                                                                                                        evento_rsvp_data: {
                                                                                                            ...prev.evento_rsvp_data,
                                                                                                            filesdocumentosdiversos: lista.length > 0 ? lista : null,
                                                                                                        }
                                                                                                    };
                                                                                                });
                                                                                            }}
                                                                                        >
                                                                                            <FaTrash size={12} />
                                                                                        </button>
                                                                                    </div>
                                                                                ))}
                                                                            </small>
                                                                        </div>
                                                                    )}
                                                                    {arquivosSalvos.length > 0 && (
                                                                        <div className="mt-2">
                                                                            <small className="text-muted">
                                                                                Arquivos salvos:
                                                                                {arquivosSalvos.map((arquivo, index) => (
                                                                                    <div key={index} className="ms-1 d-flex align-items-center">
                                                                                        <a
                                                                                            href={`${process.env.REACT_APP_API_URL}/arquivo/download/${arquivo.id}`}
                                                                                            target="_blank"
                                                                                            rel="noopener noreferrer"
                                                                                            className="text-primary text-decoration-none me-2"
                                                                                        >
                                                                                            • {arquivo.nome}
                                                                                        </a>
                                                                                        <button
                                                                                            type="button"
                                                                                            className="btn btn-sm btn-outline-danger"
                                                                                            onClick={() => {
                                                                                                const arquivoIdStr = String(arquivo.id);
                                                                                                setArquivosSalvos(prev => prev.filter(a => String(a.id) !== arquivoIdStr));
                                                                                                setDados(prev => {
                                                                                                    const idsAtuais = String(prev.evento_rsvp_data?.documentos_diversos || '').split(',').map(s => s.trim()).filter(Boolean);
                                                                                                    const novosIds = idsAtuais.filter(i => i !== arquivoIdStr);
                                                                                                    return {
                                                                                                        ...prev,
                                                                                                        evento_rsvp_data: {
                                                                                                            ...prev.evento_rsvp_data,
                                                                                                            documentos_diversos: novosIds.length > 0 ? novosIds.join(',') : null,
                                                                                                        }
                                                                                                    };
                                                                                                });
                                                                                            }}
                                                                                            title="Deletar arquivo"
                                                                                            disabled={!campoPodeEditar('documentos_diversos')}
                                                                                        >
                                                                                            <FaTrash size={12} />
                                                                                        </button>
                                                                                    </div>
                                                                                ))}
                                                                            </small>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="row mt-3">
                                                            <div className="col-md-6">
                                                                <div className="form-group">
                                                                    {labelObservacao('agenda_previa', 'Agenda Prévia')}
                                                                    <select
                                                                        className="form-select mb-2"
                                                                        value={agendaPreviaMode || ''}
                                                                        disabled={!campoPodeEditar('agenda_previa') || agendaPreviaBloqueada}
                                                                        onChange={(e) => setAgendaPreviaMode(e.target.value || null)}
                                                                    >
                                                                        <option value="" hidden>Selecione...</option>
                                                                        <option value="arquivo">Arquivo</option>
                                                                        <option value="texto">Texto</option>
                                                                    </select>
                                                                    {agendaPreviaMode === 'arquivo' && (
                                                                        <>
                                                                        <div className='d-flex align-items-center gap-2'>
                                                                            <input
                                                                                multiple={false}
                                                                                ref={agendaPreviaInputRef}
                                                                                type="file"
                                                                                className="form-control "
                                                                                disabled={!campoPodeEditar('agenda_previa')}
                                                                                onChange={(e) => {
                                                                                    const file = e.target.files[0] || null;
                                                                                    setDados({
                                                                                        ...dados,
                                                                                        evento_rsvp_data: {
                                                                                            ...dados.evento_rsvp_data,
                                                                                            fileagendaprevia: file,
                                                                                        }
                                                                                    });
                                                                                }}
                                                                            />
                                                                            {dados.evento_rsvp_data?.fileagendaprevia &&
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-outline-danger "
                                                                                    title="Remover arquivo"
                                                                                    onClick={() => {
                                                                                        const rsvp = { ...(dados.evento_rsvp_data || {}) };
                                                                                        delete rsvp.fileagendaprevia;
                                                                                        if (agendaPreviaInputRef?.current)
                                                                                            agendaPreviaInputRef.current.value = "";
                                                                                        setDados({ ...dados, evento_rsvp_data: rsvp });
                                                                                    }}
                                                                                >
                                                                                    <FaTrash size={10} />
                                                                                </button>
                                                                            }
                                                                        </div>

                                                                            {!dados.evento_rsvp_data?.fileagendaprevia && dados.evento_rsvp_data?.agenda_previa_arquivo?.nome && (
                                                                                <div className="mt-2 d-flex align-items-center justify-content-between">
                                                                                    <small className="text-muted">
                                                                                        <strong>Arquivo atual:</strong>
                                                                                        <a
                                                                                            href={`${process.env.REACT_APP_API_URL}/arquivo/download/${dados.evento_rsvp_data.agenda_previa_arquivo.id}`}
                                                                                            target="_blank"
                                                                                            rel="noopener noreferrer"
                                                                                            className="text-primary text-decoration-none ms-1"
                                                                                        >
                                                                                            {dados.evento_rsvp_data.agenda_previa_arquivo.nome.split('\\').pop().split('/').pop()}
                                                                                        </a>
                                                                                    </small>
                                                                                    <button
                                                                                        type="button"
                                                                                        className="btn btn-sm btn-outline-danger"
                                                                                        title="Remover arquivo"
                                                                                        onClick={() => {
                                                                                            setDados({
                                                                                                ...dados,
                                                                                                evento_rsvp_data: {
                                                                                                    ...dados.evento_rsvp_data,
                                                                                                    agenda_previa: null,
                                                                                                    agenda_previa_arquivo: null,
                                                                                                }
                                                                                            });
                                                                                        }}
                                                                                    >
                                                                                        <FaTrash size={10} />
                                                                                    </button>
                                                                                </div>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                    {agendaPreviaMode === 'texto' && (
                                                                        <textarea
                                                                            className="form-control"
                                                                            rows="5"
                                                                            placeholder="Digite a agenda prévia..."
                                                                            value={dados.evento_rsvp_data?.agenda_previa_texto || ''}
                                                                            disabled={!campoPodeEditar('agenda_previa_texto')}
                                                                            onChange={(e) => {
                                                                                const rsvp = { ...(dados.evento_rsvp_data || {}) };
                                                                                rsvp.agenda_previa_texto = e.target.value;
                                                                                setDados({ ...dados, evento_rsvp_data: rsvp });
                                                                            }}
                                                                        />
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="row mt-3">
                                                            <div className="col-md-12">
                                                                <div className="form-group">
                                                                    {labelObservacao('emails_copia', 'E-mails para Cópia')}
                                                                    <textarea
                                                                        className="form-control"
                                                                        rows="3"
                                                                        placeholder="Informe os e-mails separados por vírgula (ex: email1@exemplo.com, email2@exemplo.com)"
                                                                        value={dados.evento_rsvp_data?.emails_copia || ''}
                                                                        disabled={!campoPodeEditar('emails_copia')}
                                                                        onChange={(e) => {
                                                                            const rsvp = dados.evento_rsvp_data || {};
                                                                            rsvp.emails_copia = e.target.value;
                                                                            setDados({ ...dados, evento_rsvp_data: rsvp });
                                                                        }}
                                                                    />
                                                                    <small className="form-text text-muted">
                                                                        Separe múltiplos e-mails por vírgula
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        </>
                                                    ) : (
                                                        <div className="alert alert-warning mb-0">RSVP desativado para este evento.</div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Seção: Controle de Cotas */}
                                            {(dados.rsvp ?? 'nao') === 'sim' && (
                                                <div className="card mb-4">
                                                    <div className="card-header d-flex align-items-center gap-4">
                                                        <h5 className="mb-0" style={{ padding: '8px' }}>🎯 Controle de Cotas</h5>
                                                        <div className="d-flex gap-3">
                                                            <div className="form-check mb-0">
                                                                <input
                                                                    type="checkbox"
                                                                    className="form-check-input"
                                                                    id="cc_a_definir"
                                                                    checked={aDefinir('controle_cotas') || naoSeAplica('controle_cotas')}
                                                                    onChange={(e) => {
                                                                        atualizarADefinir('controle_cotas', e.target.checked)
                                                                        if(!e.target.checked && naoSeAplica('controle_cotas'))
                                                                            atualizarNaoSeAplica('controle_cotas')
                                                                    }}
                                                                />
                                                                <label className="form-check-label mb-0" htmlFor="cc_a_definir">A definir</label>
                                                            </div>
                                                            <div className="form-check mb-0">
                                                                <input
                                                                    type="checkbox"
                                                                    className="form-check-input"
                                                                    id="cc_na"
                                                                    checked={naoSeAplica('controle_cotas')}
                                                                    // N/A não pode ser APLICADO sobre um campo já preenchido (aplicar limparia o
                                                                    // arquivo). Como N/A e arquivo são mutuamente exclusivos, travar quando há arquivo
                                                                    // só impede marcar; desmarcar (quando é N/A, sem arquivo) segue livre.
                                                                    disabled={!!dados.evento_rsvp_data?.arquivo_controle_cotas?.id || !!dados.evento_rsvp_data?.filecontrolecotas}
                                                                    onChange={(e) => {
                                                                        atualizarNaoSeAplica('controle_cotas', e.target.checked)
                                                                    }}
                                                                />
                                                                <label className="form-check-label mb-0" htmlFor="cc_na">N/A</label>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="card-body">
                                                        <div className="row mt-3">
                                                            <div className="col-md-12">
                                                                <div className="form-group">
                                                                    {labelObservacao('arquivocontrolecotas', <>Arquivo Controle de Cotas {obrigatorio}</>)}
                                                                    <input
                                                                        ref={controleCoatasArquivoInputRef}
                                                                        type="file"
                                                                        className="form-control"
                                                                        disabled={!campoPodeEditar('arquivocontrolecotas') || aDefinir('controle_cotas') || naoSeAplica('controle_cotas')}
                                                                        onChange={(e) => {
                                                                            const file = e.target.files[0] || null;
                                                                            setDados({
                                                                                ...dados,
                                                                                evento_rsvp_data: {
                                                                                    ...dados.evento_rsvp_data,
                                                                                    filecontrolecotas: file,
                                                                                }
                                                                            });
                                                                        }}
                                                                    />
                                                                    {dados.evento_rsvp_data?.arquivo_controle_cotas?.nome && (
                                                                        <div className="mt-2">
                                                                            <small className="text-muted">
                                                                                <a
                                                                                    href={`${process.env.REACT_APP_API_URL}/arquivo/download/${dados.evento_rsvp_data.arquivo_controle_cotas.id}`}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    className="text-primary text-decoration-none ms-1"
                                                                                >
                                                                                    {dados.evento_rsvp_data.arquivo_controle_cotas.nome.split('\\').pop().split('/').pop()}
                                                                                </a>
                                                                            </small>
                                                                        </div>
                                                                    )}
                                                                    {renderArquivoRascunho('filecontrolecotas', controleCoatasArquivoInputRef)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="row">
                                                            <div className="col-md-12">
                                                                <div className="form-group">
                                                                    {labelObservacao('observacoes_controle_cotas', 'Observações Controle de Cotas')}
                                                                    <textarea
                                                                        className="form-control"
                                                                        rows="4"
                                                                        value={dados.evento_rsvp_data?.observacoes_controle_cotas || ''}
                                                                        disabled={!campoPodeEditar('observacoes_controle_cotas') || naoSeAplica('controle_cotas')}
                                                                        onChange={(e) => {
                                                                            const rsvp = dados.evento_rsvp_data || {};
                                                                            rsvp.observacoes_controle_cotas = e.target.value;
                                                                            setDados({ ...dados, evento_rsvp_data: rsvp });
                                                                            if(e.target.value === "N/A")
                                                                                atualizarNaoSeAplica('controle_cotas', true)
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            {/* Observações Gerais */}
                                            {((dados.rsvp ?? 'nao') === 'sim') && (
                                                <div className="card mb-4">
                                                    <div className="card-header">
                                                        <h5 className="mb-0" style={{ padding: '8px' }}>📝 Observações Gerais</h5>
                                                    </div>
                                                    <div className="card-body">
                                                        <div className="row">
                                                            <div className="col-md-12">
                                                                <div className="form-group">
                                                                    {labelObservacao('observacoes_gerais', 'Observações Gerais')}
                                                                    <textarea
                                                                        className="form-control"
                                                                        rows="5"
                                                                        placeholder="Informe observações gerais sobre o evento RSVP..."
                                                                        value={dados.evento_rsvp_data?.observacoes_gerais || ''}
                                                                        disabled={!campoPodeEditar('observacoes_gerais')}
                                                                        onChange={(e) => {
                                                                            const rsvp = dados.evento_rsvp_data || {};
                                                                            rsvp.observacoes_gerais = e.target.value;
                                                                            setDados({ ...dados, evento_rsvp_data: rsvp });
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            <div className="row">
                                                <div className="col-12 mt-3">
                                                    <SaveButton isSubmitting={isSubmitting} className="btn btn-primary me-2" disabled={!podeSalvar} />
                                                    <button type="button" className="btn btn-outline-secondary" onClick={() => history.goBack()}>
                                                        Voltar
                                                    </button>
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        <Modal show={showModalRascunho} onHide={descartarRascunho} centered backdrop="static" keyboard={false}>
            <Modal.Header>
                <Modal.Title>Rascunho encontrado</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>Há um preenchimento deste RSVP que não chegou a ser salvo e ficou guardado neste navegador.</p>
                {rascunhoPendente?.campos?.salvoEm && (
                    <p className="text-muted mb-2">
                        <small>Último rascunho: {moment(rascunhoPendente.campos.salvoEm).format('DD/MM/YYYY HH:mm')}</small>
                    </p>
                )}
                <p className="mb-0">Deseja continuar de onde parou?</p>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={descartarRascunho}>
                    Descartar
                </Button>
                <Button variant="primary" onClick={restaurarRascunho}>
                    Continuar de onde parei
                </Button>
            </Modal.Footer>
        </Modal>

        <Modal show={showModalRsvpSemLocal} onHide={() => setShowModalRsvpSemLocal(false)} centered>
            <Modal.Header closeButton>
                <Modal.Title>Local não definido</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>Este evento está com o local marcado como <strong>Não Definido</strong>.</p>
                <p>Defina o local do evento antes de ativar o RSVP.</p>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="primary" onClick={() => setShowModalRsvpSemLocal(false)}>
                    Entendi
                </Button>
            </Modal.Footer>
        </Modal>

        <Modal show={showModalCamposEvento} onHide={() => setShowModalCamposEvento(false)} centered>
            <Modal.Header closeButton>
                <Modal.Title>Campos do evento não preenchidos</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {camposEventoObrigatoriosFaltantes.length > 0 && (
                    <>
                        <p>Os campos abaixo são <strong>obrigatórios</strong> e precisam ser preenchidos antes de iniciar o RSVP:</p>
                        <ul>
                            {camposEventoObrigatoriosFaltantes.map((campo, index) => (
                                <li key={`obr-${index}`}><strong>{campo}</strong></li>
                            ))}
                        </ul>
                    </>
                )}
                {camposEventoFaltantes.length > 0 && (
                    <>
                        <p>Os seguintes campos do evento estão <strong>vazios</strong>{camposEventoObrigatoriosFaltantes.length > 0 ? ' (opcionais)' : ''}:</p>
                        <ul>
                            {camposEventoFaltantes.map((campo, index) => (
                                <li key={index}>{campo}</li>
                            ))}
                        </ul>
                    </>
                )}
                {camposEventoObrigatoriosFaltantes.length > 0
                    ? <p>Preencha os campos <strong>obrigatórios</strong> para poder iniciar o RSVP.</p>
                    : <p>Deseja realmente enviar o <strong>Start de RSVP</strong> mesmo assim?</p>}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowModalCamposEvento(false)}>
                    {camposEventoObrigatoriosFaltantes.length > 0 ? 'Fechar' : 'Não, cancelar'}
                </Button>
                {camposEventoObrigatoriosFaltantes.length === 0 && (
                    <Button variant="primary" onClick={prosseguirSalvar}>
                        Sim, enviar Start
                    </Button>
                )}
            </Modal.Footer>
        </Modal>

        </ErpTemplate>
    );
}
